import React, { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    Play, Pause, CheckCircle, User, Clock, Calendar, X, ChevronDown,
    ClipboardList, ImageIcon, FileText, History, Target, Layers,
    AlertTriangle, RefreshCw, Maximize2, LogOut, Lock, FolderOpen, Settings, Star,
} from 'lucide-react';
import useAuthStore from '../stores/useAuthStore';
import { api, apiUrl } from '../lib/api';
import { Vista3DProduto, DesenhoTecnicoProduto } from '../components/ProdutoVisual';
import useBloqueioScroll from '../hooks/useBloqueioScroll';
// Planejamento diario desativado temporariamente para a implantacao em producao.
// import MeuPlanejamento from '../components/MeuPlanejamento';

// Nome do usuário + Sair — repetido nos modais de tela cheia (escolha de
// máquina/OP), que ficam por cima do cabeçalho normal (fixed inset-0 z-50) e
// escondem o "Sair" que mora lá (ver Header.jsx/MainLayout.jsx) — sem isso o
// operador fica sem forma nenhuma de deslogar enquanto esses modais estão
// abertos. Fixed no canto superior direito da TELA (fora do cartão do modal,
// z-index maior — pedido do usuário 06/08/2026), não dentro do cabeçalho dele.
function BarraUsuario({ nomeUsuario, onLogout }) {
    return (
        <div className="fixed top-5 right-5 z-[60] flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl mes-glass border border-white/[0.08] text-anthracite-300 shadow-glass">
                <User size={14} className="text-anthracite-500" />
                <span className="text-[12px] font-semibold truncate max-w-[140px]">{nomeUsuario}</span>
            </div>
            <button onClick={onLogout}
                className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/15 text-red-400 hover:bg-red-500/15 transition-all duration-200 text-[12px] font-semibold shadow-glass">
                <LogOut size={15} /> <span>Sair</span>
            </button>
        </div>
    );
}

function normalizarOrdem(row) {
    return {
        id: row.id,
        codigo: row.codigo,
        produto: row.produto,
        produtoNome: row.produto_nome || row.produto,
        roteiro: row.roteiro,
        maquina: row.maquina_nome || '—',
        quantidadeAProduzir: Number(row.quantidade),
        produzido: Number(row.produzido_total) || 0,
        refugado: Number(row.refugado_total) || 0,
        // Agendamento manual (17/08/2026, tabela ZP1) — quando o PCP marcou
        // essa OP/operação pra rodar agora nesta máquina, o backend já devolve
        // ordenado com esse item primeiro (listarOpsAbertasParaMaquina).
        agendamentoAtual: row._AGENDAMENTO_ATUAL || null,
        // Projeto Especial (29/08/2026, pedido do usuário: destacar já na
        // lista de escolha, em toda máquina por onde a OP passar — não só
        // depois de entrar nela) — ver listarOpsAbertasParaMaquina no backend.
        especial: !!row.especial,
    };
}

const MOTIVOS_PARADA = [
    'Falta de material', 'Manutenção corretiva', 'Manutenção preventiva',
    'Setup / Preparação', 'Troca de ferramenta', 'Falta de operador',
    'Problema de qualidade', 'Aguardando ordem', 'Intervalo / Refeição', 'Outros',
];

// Tipos de Hora (21/08/2026, pedido do usuário) — códigos reais do campo
// H6_TPHORA do Protheus (espelhado em ZH6_TPHORA, já confirmado existente na
// tabela física via dump real da ZH6010). Operador escolhe antes de cada
// Iniciar/Continuar Produção — caracteriza o tipo de hora daquele ciclo de
// trabalho (linha nova na ZH6, ver zh6Writer.js).
const TIPOS_HORA = [
    { codigo: '1', label: 'Produção' },
    { codigo: '2', label: 'Retrabalho' },
    { codigo: '3', label: 'Preparação' },
    { codigo: '4', label: 'Produção Especial' },
    { codigo: '5', label: 'Horas Improdutivas' },
    { codigo: '6', label: 'Repreparação' },
];

// Matéria-prima temporariamente oculta na interface. Para reativar a etapa,
// basta alterar esta constante para true; o modal e sua integração permanecem
// preservados abaixo.
const EXIBIR_MATERIAIS_OP = true;

function mensagemRequisicaoMateriais(err, materiais, itensSolicitados = []) {
    const formatar = (valor) => Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
    const detalhesEstruturados = Array.isArray(err?.detalhes)
        ? err.detalhes.filter((detalhe) => detalhe && typeof detalhe === 'object')
        : [];

    if (err?.code === 'SALDO_ESTOQUE_INSUFICIENTE' && detalhesEstruturados.length > 0) {
        const linhas = detalhesEstruturados.map((item) =>
            `Produto ${item.codigo}, armazém ${item.armazem}: saldo atual ${formatar(item.saldo_atual)} e quantidade solicitada ${formatar(item.quantidade_solicitada)}.`);
        return `Não há saldo suficiente no estoque.\n${linhas.join('\n')}\nSolicite o abastecimento ao estoque/PCP.`;
    }

    const detalhesTexto = Array.isArray(err?.detalhes)
        ? err.detalhes.filter((detalhe) => typeof detalhe === 'string').join('\n')
        : typeof err?.detalhes === 'string' ? err.detalhes : '';
    if (detalhesTexto.includes('MA240NEGAT')) {
        const produto = detalhesTexto.match(/Produto:\s*([^\s\r\n]+)/i)?.[1]?.trim();
        const local = detalhesTexto.match(/Local:\s*([^\s\r\n]+)/i)?.[1]?.trim();
        const material = (materiais || []).find((item) =>
            item.codigo === produto && (!local || item.armazem === local));
        const solicitado = (itensSolicitados || []).find((item) =>
            item.codigo === produto && (!local || item.armazem === local));
        if (produto && material) {
            return `Não há saldo suficiente no estoque. Produto ${produto}, armazém ${local || material.armazem}: saldo atual ${formatar(material.quantidade_disponivel)} e quantidade solicitada ${formatar(solicitado?.quantidade ?? material.quantidade_pendente)}. Solicite o abastecimento ao estoque/PCP.`;
        }
        return 'Não há saldo suficiente no estoque para realizar a requisição. Solicite o abastecimento ao estoque/PCP.';
    }

    // Para outras recusas, preserva a explicação devolvida pelo ExecAuto em
    // vez de trocar tudo pela mensagem genérica da rotina. Códigos internos
    // isolados não ajudam o operador e permanecem apenas no log/auditoria.
    const detalhesUteis = detalhesTexto
        .split(/\r?\n/)
        .map((linha) => linha.trim())
        .filter((linha) => linha && !/^([A-Z]{2,}\d+[A-Z0-9]*|MATA\d+)$/i.test(linha));
    if (detalhesUteis.length > 0) {
        return `O sistema não concluiu a requisição.\n${detalhesUteis.slice(0, 4).join('\n')}`;
    }
    return err?.message || 'Não foi possível requisitar os materiais da OP.';
}

function formatTime(s) {
    const h = String(Math.floor(s / 3600)).padStart(2, '0');
    const m = String(Math.floor((s % 3600) / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${h}:${m}:${sec}`;
}
function getNow() {
    return new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function ehTipoSetup(tipoHora) {
    return tipoHora === '3' || tipoHora === '6';
}

// Datas do Protheus vêm como AAAAMMDD (DTOS), sem separador — mesmo achado
// já documentado em OrdensProducao.jsx (formatDate). `new Date("20260814")`
// não é um formato que o JS reconhece e devolve "Invalid Date" (bug real
// 14/08/2026, achado pelo usuário no Histórico de Apontamentos). Fatiando a
// string direto (sem passar por Date/fuso), vira "DD/MM/AAAA" sem erro.
function formatarDataApontamento(valor) {
    if (!valor) return '--';
    const s = String(valor).trim();
    if (/^\d{8}$/.test(s)) return `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}`;
    return new Date(valor).toLocaleString('pt-BR');
}

function mapApontamento(row) {
    return {
        id: row.id,
        data: formatarDataApontamento(row.data),
        operador: row.operador,
        inicio: row.inicio,
        fim: row.fim,
        tempo: formatTime(row.tempo_segundos || 0),
        produzido: Number(row.produzido) || 0,
        refugado: Number(row.refugado) || 0,
        motivo: row.motivo,
        // Qual sequência do roteiro esse apontamento foi feito — usado pra saber
        // quais sequências já estão "liberadas" pra próxima (ver sequenciasLiberadas
        // em Producao.jsx). Vem de H6_OPERAC, pode ser null em apontamento antigo.
        operacao: row.operacao || null,
        // Justificativa de excesso sobre a meta da OP (21/08/2026) — null na
        // grande maioria dos apontamentos.
        justificativa: row.justificativa || null,
        // Tipo de Hora (21/08/2026) — código (ver TIPOS_HORA); resolvido pro
        // rótulo só na hora de exibir (ModalHistorico), pra não duplicar a
        // lista de nomes em dois lugares.
        tipoHora: row.tipo_hora || null,
    };
}

// Máquina não é mais escolhida no login (04/08/2026, decisão do usuário) — vem
// dos centros de trabalho liberados pro usuário (Gestão de Usuários). Se já
// existir uma resolvida, o backend entrega ela pronta (user.maquina); esta
// tela aparece sempre que a máquina NÃO veio resolvida (mais de uma candidata,
// persistência antiga, ou nenhuma com OP aberta agora) — o operador escolhe a
// máquina e segue pra escolha de OP/sequência (17/09/2026, pedido do usuário:
// nunca mais travar com "Seu usuário não tem nenhuma máquina liberada.").
function ModalSelecionarMaquinaLogin({ maquinas, onConfirmar, nomeUsuario, onLogout, carregando = false, onVerificar }) {
    useBloqueioScroll();
    // createPortal (não só z-50 + fixed): a sidebar/header de MainLayout
    // ficam fora dessa árvore, mas o wrapper de conteúdo dele tem `relative
    // z-10` — isso cria um contexto de empilhamento próprio, e QUALQUER
    // z-index interno (mesmo z-50) nunca consegue superar a sidebar (z-40),
    // que é irmã desse wrapper na raiz. Achado real 13/08/2026: com o overlay
    // opaco (bg-anthracite-950) a sidebar continuava aparecendo por cima.
    // Portal pro document.body tira o modal dessa árvore, mesmo padrão já
    // usado no lightbox de imagem (ver createPortal mais abaixo no arquivo).
    return createPortal(
        <>
            <BarraUsuario nomeUsuario={nomeUsuario} onLogout={onLogout} />
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-anthracite-950 p-4">
                <div className="mes-modal w-full max-w-lg overflow-hidden animate-slide-up rounded-3xl">
                    <div className="p-6 border-b border-white/[0.06]">
                        <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-3">
                            <div className="bg-brand-500/10 p-2.5 rounded-xl">
                                <Target className="text-brand-400" size={20} />
                            </div>
                            Em qual máquina você está?
                        </h2>
                        <p className="text-[12px] text-anthracite-500 mt-1.5 ml-12">Escolha a máquina em que você vai trabalhar para ver as ordens de produção dela.</p>
                    </div>
                    <div className="p-5 max-h-[55vh] overflow-y-auto space-y-3">
                        {carregando ? (
                            <p className="text-[13px] text-anthracite-500 text-center py-8">Carregando máquinas...</p>
                        ) : maquinas.length === 0 ? (
                            <div className="text-center py-8 space-y-3">
                                <p className="text-[13px] text-anthracite-500">Nenhuma máquina disponível no momento.</p>
                                {onVerificar && (
                                    <button onClick={onVerificar} className="flex items-center gap-1.5 mx-auto px-4 py-2 rounded-xl border border-white/[0.06] text-anthracite-300 text-[12px] font-medium hover:bg-white/[0.04] transition-colors">
                                        <RefreshCw size={14} /> Verificar de novo
                                    </button>
                                )}
                            </div>
                        ) : maquinas.map((m) => (
                            <button key={m.codigo} onClick={() => onConfirmar(m)}
                                className="w-full text-left p-5 rounded-2xl border border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.02] transition-all duration-200">
                                <span className="text-[14px] font-bold text-anthracite-100">{m.codigo} - {m.nome}</span>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

// Aviso de OP em andamento (06/08/2026, pedido explícito do usuário: "quero
// que quando eu entre no sistema ele reconheça meu usuário e me diga que já
// existe uma OP sendo iniciada por mim, e ao clicar ok eu ir direto pra tela
// de apontamento da OP") — substitui o "pular direto sozinho" de uma tentativa
// anterior, que o usuário achou que não tinha funcionado por não avisar nada.
// Sem opção de "escolher outra OP" (pedido explícito do usuário, mesma data):
// se já tem uma em andamento, o único caminho é retomar ela — só o botão OK.
// isSetup (21/08/2026, pedido do usuário: "mesmo padrão da OP", agora
// cobrindo setup também) — setup não aparece na ZH6 (buscarOpEmAndamentoPorOperador,
// zh6Writer.js), então precisa de uma checagem à parte na ZS1
// (buscarSetupEmAndamentoPorOperador, setup.js); o aviso é o MESMO modal, só
// troca o texto.
// maquinaDiferente (25/08/2026, pedido do usuário: avisar em QUALQUER
// máquina, não só na que já tinha algo aberto) — quando preenchido, a OP/
// setup em andamento está numa máquina diferente da que o operador está
// agora; o texto cita o nome dela em vez do genérico "nesta máquina".
function ModalOpEmAndamento({ codigo, produtoNome, isSetup, maquinaDiferente, onConfirmar, nomeUsuario, onLogout }) {
    useBloqueioScroll();
    const localTexto = maquinaDiferente ? <>na máquina <span className="font-semibold text-anthracite-300">{maquinaDiferente}</span></> : 'nesta máquina';
    // createPortal — mesmo motivo do ModalSelecionarMaquinaLogin acima (sem
    // isso, a sidebar de MainLayout aparece por cima do overlay).
    return createPortal(
        <>
            <BarraUsuario nomeUsuario={nomeUsuario} onLogout={onLogout} />
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-anthracite-950 p-4">
                <div className="mes-modal w-full max-w-md overflow-hidden animate-slide-up rounded-3xl">
                    <div className="p-6 border-b border-white/[0.06]">
                        <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-3">
                            <div className="bg-amber-500/10 p-2.5 rounded-xl">
                                <AlertTriangle className="text-amber-400" size={20} />
                            </div>
                            {isSetup ? 'Você já tem um setup em andamento' : 'Você já tem uma OP em andamento'}
                        </h2>
                        <p className="text-[12px] text-anthracite-500 mt-1.5 ml-12">
                            {isSetup ? (
                                <>O setup da ordem <span className="font-semibold text-anthracite-300">{codigo}</span>
                                {produtoNome ? <>, {produtoNome},</> : ''} já foi iniciado por você {localTexto}.</>
                            ) : (
                                <>A ordem <span className="font-semibold text-anthracite-300">{codigo}</span>
                                {produtoNome ? <>, {produtoNome},</> : ''} já foi iniciada por você {localTexto}.</>
                            )}
                        </p>
                    </div>
                    <div className="p-6">
                        <button onClick={onConfirmar} className="w-full py-3.5 rounded-2xl font-semibold text-[13px] mes-btn-primary">
                            {isSetup ? 'OK, continuar esse setup' : 'OK, continuar essa OP'}
                        </button>
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
}

// Painel de OP (esquerda) + painel de sequência (direita, pedido do usuário
// 04/08/2026: "quando eu clicar na OP... um modal no lado direito pra
// selecionar a sequência", os dois lado a lado na mesma tela). O painel da
// direita só aparece depois de clicar numa OP (onSelecionarOP), mostrando as
// sequências já liberadas pra máquina do operador nessa OP específica — nunca
// o roteiro inteiro (a mesma máquina pode aparecer em várias sequências do
// mesmo roteiro, 1449 casos confirmados com dado real) e nunca uma sequência
// ainda não liberada pela etapa anterior (ver sequenciasLiberadas).
function ModalSelecionarOP({
    ordens, selecionada, onSelecionarOP, onConfirmar, podeConfirmar, onFechar, maquinaNome, onTrocarMaquina,
    carregandoSequencias, sequencias, sequenciaEscolhida, onEscolherSequencia, semOperacaoNoRoteiro, aguardandoEtapaAnterior,
    todasOperacoesJaConcluidas, produzidoPorOperacaoPreview, metaPorOperacaoPreview, nomeUsuario, onLogout,
}) {
    useBloqueioScroll();
    // createPortal — mesmo motivo do ModalSelecionarMaquinaLogin (topo do
    // arquivo): sem isso, a sidebar de MainLayout aparece por cima do overlay.
    return createPortal(
        <>
            <BarraUsuario nomeUsuario={nomeUsuario} onLogout={onLogout} />
            <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-anthracite-950 p-4">
                {/* Planejamento diario desativado temporariamente para a implantacao em producao. */}
                {/* <div className="w-full max-w-4xl mb-3"><MeuPlanejamento /></div> */}
                <div className="flex items-start justify-center gap-4">
                    <div className="mes-modal w-full max-w-lg overflow-hidden animate-slide-up rounded-3xl flex flex-col max-h-[85vh]">
                        {/* min-h-[150px] (14/08/2026, achado real do usuário: linha
                            divisória dos dois painéis lado a lado desalinhada) — título
                            e subtítulo podem quebrar em 1 ou 2 linhas dependendo do
                            texto (nome da máquina varia, e o painel da direita é mais
                            estreito, quebra o título mais fácil). Sem uma altura mínima
                            fixa e IGUAL nos dois cabeçalhos, a linha embaixo cai numa
                            altura diferente em cada painel toda vez que o texto muda. */}
                        <div className="min-h-[150px] p-6 border-b border-white/[0.06] flex items-start justify-between">
                            <div>
                                <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-3">
                                    <div className="bg-brand-500/10 p-2.5 rounded-xl shrink-0">
                                        <ClipboardList className="text-brand-400" size={20} />
                                    </div>
                                    Selecionar Ordem de Produção
                                </h2>
                                <p className="text-[12px] text-anthracite-500 mt-1.5 ml-12">
                                    Escolha a OP que será trabalhada nesta estação{maquinaNome ? `, máquina ${maquinaNome}` : ''}.
                                </p>
                            </div>
                            {onFechar && <button onClick={onFechar} className="p-2.5 rounded-xl hover:bg-white/[0.06] text-anthracite-500 transition-all duration-200 shrink-0"><X size={18} /></button>}
                        </div>
                        <div className="p-5 flex-1 overflow-y-auto space-y-3">
                            {ordens.length === 0 && (
                                <div className="text-center py-8 space-y-3">
                                    <p className="text-[13px] text-anthracite-500">Nenhuma OP em aberto pra essa máquina no momento.</p>
                                    {onTrocarMaquina && (
                                        <button onClick={onTrocarMaquina} className="text-[12px] font-semibold text-brand-400 hover:text-brand-300">
                                            Trocar de máquina
                                        </button>
                                    )}
                                </div>
                            )}
                            {ordens.map(o => {
                                // Barra de progresso (13/08/2026, pedido do usuário): antes
                                // aparecia em TODA OP da lista, somando produzido da OP inteira
                                // (ZH6 por OP, cruza operações) — ambíguo antes de saber qual
                                // sequência vai ser feita, e podia até ficar errado se mais de
                                // uma operação já tivesse produção (mesmo lote contado 2x, igual
                                // outros bugs já corrigidos nesta sessão). Agora só aparece na OP
                                // selecionada, e só depois de escolher a sequência — com o
                                // produzido DAQUELA operação específica (produzidoPorOperacaoPreview,
                                // já buscado no clique/preview da OP).
                                const isSelecionada = selecionada?.codigo === o.codigo;
                                const mostrarProgresso = isSelecionada && !!sequenciaEscolhida;
                                const produzidoNaSequencia = mostrarProgresso
                                    ? (produzidoPorOperacaoPreview?.[sequenciaEscolhida.G2_OPERAC] || 0)
                                    : 0;
                                // Meta ajustada (14/08/2026): a 1ª operação do roteiro pode ter
                                // meta maior que o.quantidadeAProduzir (flat) quando há refugo
                                // em operações posteriores — ver meta_por_operacao/obterSessao.
                                const metaNaSequencia = mostrarProgresso
                                    ? (metaPorOperacaoPreview?.[sequenciaEscolhida.G2_OPERAC] ?? o.quantidadeAProduzir)
                                    : o.quantidadeAProduzir;
                                return (
                                    <button key={o.codigo} onClick={() => onSelecionarOP(o)}
                                        className={`w-full text-left p-5 rounded-2xl border transition-all duration-200
                                    ${isSelecionada ? 'border-brand-500/50 bg-brand-500/10 shadow-sm' : o.especial ? 'border-yellow-400/60 bg-yellow-400/5 hover:border-yellow-400/80' : o.agendamentoAtual ? 'border-roxo-500/40 bg-roxo-500/5 hover:border-roxo-500/60' : 'border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.02]'}`}>
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[14px] font-bold text-anthracite-100">{o.codigo}</span>
                                                    {/* Projeto Especial (29/08/2026, pedido do usuário) — precisa
                                                        aparecer já aqui, antes de escolher, em toda máquina por
                                                        onde a OP passar (o dado já vem certo por máquina, ver
                                                        listarOpsAbertasParaMaquina). */}
                                                    {o.especial && (
                                                        <span className="flex items-center gap-1 text-[9px] font-bold text-anthracite-900 bg-yellow-400 px-2 py-0.5 rounded-lg uppercase tracking-wide">
                                                            <Star size={9} fill="currentColor" /> Projeto Especial
                                                        </span>
                                                    )}
                                                    {o.agendamentoAtual && (
                                                        <span className="text-[9px] font-bold text-roxo-300 bg-roxo-500/15 px-2 py-0.5 rounded-lg uppercase tracking-wide">
                                                            Programada · {o.agendamentoAtual.inicio ? new Date(o.agendamentoAtual.inicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-[12px] text-anthracite-500 mt-0.5">{o.produtoNome}</p>
                                            </div>
                                            <div className="text-right">
                                                <span className="block text-[10px] text-anthracite-500 uppercase">Máquina</span>
                                                <span className="text-[12px] font-semibold text-anthracite-300">{maquinaNome || o.maquina}</span>
                                            </div>
                                        </div>
                                        {mostrarProgresso && (
                                            <div className="mt-3 flex items-center gap-3">
                                                <div className="flex-1 bg-surface-3/50 rounded-full h-2 overflow-hidden">
                                                    <div className="bg-brand-500 h-2 rounded-full" style={{ width: `${Math.min((produzidoNaSequencia / metaNaSequencia) * 100, 100)}%` }} />
                                                </div>
                                                <span className="text-[11px] font-medium text-anthracite-500 whitespace-nowrap">{produzidoNaSequencia}/{metaNaSequencia}</span>
                                            </div>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                        <div className="p-6 border-t border-white/[0.06] space-y-3">
                            <button onClick={onConfirmar} disabled={!podeConfirmar}
                                className={`w-full py-3.5 rounded-2xl font-semibold text-[13px] transition-all duration-200
                            ${podeConfirmar ? 'mes-btn-primary' : 'bg-surface-3 text-anthracite-500 cursor-not-allowed'}`}>
                                Confirmar Seleção
                            </button>
                            {ordens.length > 0 && onTrocarMaquina && (
                                <button onClick={onTrocarMaquina} className="w-full py-3.5 rounded-2xl font-semibold text-[13px] mes-btn-ghost border border-white/[0.06]">
                                    Trocar de máquina
                                </button>
                            )}
                        </div>
                    </div>

                    {selecionada && (
                        <div className="mes-modal w-full max-w-sm overflow-hidden animate-slide-in-right rounded-3xl flex flex-col max-h-[85vh]">
                            {/* min-h-[150px] — mesmo valor do painel da esquerda (ver
                                comentário lá), pra linha divisória sempre bater certo entre
                                os dois, seja qual for o tamanho do nome da máquina/OP. */}
                            <div className="min-h-[150px] p-6 border-b border-white/[0.06]">
                                <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-3">
                                    <div className="bg-brand-500/10 p-2.5 rounded-xl shrink-0">
                                        <Target className="text-brand-400" size={20} />
                                    </div>
                                    Qual sequência você vai fazer?
                                </h2>
                                <p className="text-[12px] text-anthracite-500 mt-1.5 ml-12">OP {selecionada.codigo}</p>
                            </div>
                            <div className="flex-1 p-5 overflow-y-auto space-y-3">
                                {carregandoSequencias && (
                                    <p className="text-[13px] text-anthracite-500 text-center py-8">Carregando sequências do roteiro...</p>
                                )}
                                {!carregandoSequencias && semOperacaoNoRoteiro && (
                                    <p className="text-[12px] text-amber-400">Essa máquina não está no roteiro desta OP; o apontamento local segue normal, mas o envio ao ERP pode ser recusado por falta de operação.</p>
                                )}
                                {!carregandoSequencias && aguardandoEtapaAnterior && (
                                    <p className="text-[12px] text-amber-400">Sua máquina participa do roteiro desta OP, mas a etapa anterior ainda não teve nenhum apontamento; aguardando liberação.</p>
                                )}
                                {!carregandoSequencias && todasOperacoesJaConcluidas && (
                                    <p className="text-[12px] text-petrol-400">Todas as sequências desta OP na sua máquina já foram produzidas por completo (apontamento real já bateu a quantidade, mesmo sem totalizar no ERP), não há mais nada a fazer aqui.</p>
                                )}
                                {/* Sequência com agendamento pra agora (17/08/2026, tabela ZP1)
                                    ganha o mesmo destaque roxo usado na lista de OPs — vem já
                                    ordenada primeiro pelo backend (candidatosSequencia mantém a
                                    ordem de `sequencias`, sem reordenar de novo aqui). */}
                                {!carregandoSequencias && [...sequencias].sort((a, b) => {
                                    const aAgendada = selecionada?.agendamentoAtual?.operacao === a.G2_OPERAC;
                                    const bAgendada = selecionada?.agendamentoAtual?.operacao === b.G2_OPERAC;
                                    return (bAgendada ? 1 : 0) - (aAgendada ? 1 : 0);
                                }).map((seq) => {
                                    const agendada = selecionada?.agendamentoAtual?.operacao === seq.G2_OPERAC;
                                    return (
                                        <button key={seq.G2_OPERAC} onClick={() => onEscolherSequencia(seq)}
                                            className={`w-full text-left p-5 rounded-2xl border transition-all duration-200
                                        ${sequenciaEscolhida?.G2_OPERAC === seq.G2_OPERAC ? 'border-brand-500/50 bg-brand-500/10 shadow-sm' : agendada ? 'border-roxo-500/40 bg-roxo-500/5 hover:border-roxo-500/60' : 'border-white/[0.06] hover:border-white/[0.12] hover:bg-white/[0.02]'}`}>
                                            <div className="flex items-center gap-2">
                                                <span className="text-[14px] font-bold text-anthracite-100">Sequência {seq.G2_OPERAC}</span>
                                                {agendada && (
                                                    <span className="text-[9px] font-bold text-roxo-300 bg-roxo-500/15 px-2 py-0.5 rounded-lg uppercase tracking-wide">Programada</span>
                                                )}
                                            </div>
                                            <p className="text-[12px] text-anthracite-500 mt-0.5">{(seq.G2_DESCRI || '').trim() || '—'}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>,
        document.body
    );
}

// Setup obrigatório (21/08/2026, pedido do usuário): nenhuma operação pode
// produzir sem passar antes por um ciclo de Preparação (Tipo de Hora 3).
// `exigeSetup` (calculado em Producao.jsx a partir de `setupFeito`, ver
// obterSessao no backend) trava as outras opções até isso acontecer pelo
// menos 1 vez para a operação atual — depois disso, fica livre pra sempre
// (não precisa repetir a cada novo ciclo de trabalho).
//
// Preparação vira mão única (24/08/2026, pedido do usuário): uma vez que o
// setup já foi feito pra essa operação, "Preparação" (3) NUNCA mais volta a
// aparecer disponível — só existe 1 primeira preparação por operação. Se
// precisar refazer o setup depois (ex.: troca de ferramenta no meio do
// trabalho), o tipo certo passa a ser "Repreparação" (6), não Preparação de
// novo — por isso o mesmo `exigeSetup`/`!exigeSetup` também bloqueia o card 3
// (não só libera os outros) depois que o setup já aconteceu.
// podePreparar (25/08/2026, pedido do usuário: "Preparador" é uma permissão
// do funcionário, não um perfil novo — nem todo operador pode fazer setup,
// mas todo Preparador pode operar normalmente) — quando a operação exige
// setup (`exigeSetup`), o componente nem mostra mais o grid de cards (não
// fazia sentido "escolher" entre 6 opções com 5 travadas): vira um aviso
// direto, com 2 variantes —
//   (1) operador SEM a permissão (`!podePreparar`): bloqueia de vez, só
//       "Entendi" pra fechar, sem confirmar nada — precisa de um Preparador.
//   (2) operador COM a permissão: aviso "é necessário iniciar o Setup", com
//       Confirmar já mandando tipo '3' direto (única opção possível mesmo).
// O grid de cards (mais abaixo) só é alcançado quando `!exigeSetup`.
function ModalTipoHora({ onConfirmar, onFechar, onVoltarSelecao, tipoHoraAtual, exigeSetup, podePreparar }) {
    useBloqueioScroll();
    // exigeSetup nunca chega a usar este state — os 2 ramos de exigeSetup
    // voltam mais cedo (abaixo), sem passar pelo grid de cards.
    const [tipoHora, setTipoHora] = useState(tipoHoraAtual || '');
    const [erro, setErro] = useState('');
    const handleConfirmar = () => {
        if (!tipoHora) { setErro('Selecione o tipo de hora.'); return; }
        onConfirmar(tipoHora);
    };

    if (exigeSetup && !podePreparar) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                <div className="mes-modal w-full max-w-lg p-7 animate-slide-up">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-2">
                            <Lock size={22} className="text-amber-400" /> Setup necessário
                        </h2>
                        <button onClick={onVoltarSelecao} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                    </div>
                    <p className="text-[13px] text-anthracite-400">
                        Esta operação ainda não passou pelo setup (Preparação) e seu usuário não tem permissão pra fazer setup.
                        Peça pra um Preparador iniciar o setup desta operação antes de continuar.
                    </p>
                    <div className="mt-6">
                        <button onClick={onVoltarSelecao} className="w-full mes-btn-primary py-3">Entendi</button>
                    </div>
                </div>
            </div>
        );
    }

    // exigeSetup && podePreparar (25/08/2026, pedido do usuário: trocar a
    // telinha de escolha — com 5 dos 6 cards travados, só sobrava 1 opção
    // possível mesmo, então nem faz sentido pedir pra "escolher"). Vira um
    // aviso direto "precisa iniciar o Setup", com 1 botão só — sem grid de
    // cards. `onConfirmar('3')` direto, sem depender do estado `tipoHora`
    // (aqui só existe um valor possível de qualquer forma).
    if (exigeSetup) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                <div className="mes-modal w-full max-w-lg p-7 animate-slide-up">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-2">
                            <Clock size={22} className="text-amber-400" /> Setup necessário
                        </h2>
                        <button onClick={onVoltarSelecao} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                    </div>
                    <p className="text-[13px] text-anthracite-400">
                        Esta operação ainda não passou pelo setup. É necessário iniciar o Setup (Preparação) antes de produzir.
                    </p>
                    <div className="flex gap-3 mt-6">
                        <button onClick={onVoltarSelecao} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Cancelar</button>
                        <button onClick={() => onConfirmar('3')} className="flex-1 mes-btn-primary bg-petrol-600 hover:bg-petrol-500 py-3">Confirmar</button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-lg p-7 animate-slide-up">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-2">
                        <Clock size={22} className="text-petrol-400" /> Tipo de Hora
                    </h2>
                    <button onClick={onVoltarSelecao} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                </div>
                {/* Chegar aqui só acontece com !exigeSetup (os 2 casos de exigeSetup
                    voltam mais cedo, ver acima) — setup já feito, "Preparação" some
                    da lista pra sempre (mão única, ver bloqueado abaixo). */}
                <p className="text-[12px] text-anthracite-500 mb-4">
                    Selecione o tipo de hora desta operação. Depois, clique em "Iniciar Produção" pra começar.
                    {' '}Setup já feito. Pra refazer, use Repreparação.
                </p>
                {/* Layout em 1 linha de 3 cartões verticais (27/08/2026, pedido do
                    usuário) — a grade de 2 colunas antiga sobrava um card sozinho
                    numa 2ª linha desde que Preparação/Produção Especial/Horas
                    Improdutivas saíram da lista (restam só 3 opções). */}
                <div className="grid grid-cols-3 gap-3">
                    {/* Preparação (3), Produção Especial (4) e Horas Improdutivas (5)
                        removidas da lista de escolha (27/08/2026, pedido do usuário) —
                        Preparação já nunca era de fato selecionável aqui mesmo (mão
                        única, forçada só pelas telas de "Setup necessário" acima); as
                        outras duas saíram por não fazerem mais sentido como opção. A
                        lista completa (TIPOS_HORA) continua existindo pra resolver o
                        rótulo de apontamentos antigos no histórico (ver mais abaixo). */}
                    {TIPOS_HORA.filter(t => !['3', '4', '5'].includes(t.codigo)).map(t => {
                        // Repreparação (25/08/2026, pedido do usuário): é um setup novo,
                        // então só quem tem a permissão de preparador pode escolher.
                        const bloqueado = t.codigo === '6' && !podePreparar;
                        return (
                            <button
                                key={t.codigo}
                                disabled={bloqueado}
                                onClick={() => { setTipoHora(t.codigo); setErro(''); }}
                                className={`flex flex-col items-center justify-center gap-2.5 text-center px-3 py-5 rounded-xl border text-[13px] font-semibold transition-colors
                                    ${bloqueado
                                        ? 'border-white/[0.03] text-anthracite-600 opacity-40 cursor-not-allowed'
                                        : tipoHora === t.codigo
                                            ? 'border-petrol-500 bg-petrol-500/10 text-petrol-300'
                                            : 'border-white/[0.06] text-anthracite-400 hover:bg-white/[0.04]'}`}>
                                <span className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-bold tabular-nums
                                    ${!bloqueado && tipoHora === t.codigo ? 'bg-petrol-500/20 text-petrol-300' : 'bg-white/[0.05] text-anthracite-500'}`}>
                                    {t.codigo}
                                </span>
                                {t.label}
                            </button>
                        );
                    })}
                </div>
                {erro && <p className="text-red-400 text-[12px] mt-3">{erro}</p>}
                <div className="flex gap-3 mt-6">
                    <button onClick={onVoltarSelecao} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Cancelar</button>
                    {/* Confirmar só habilita depois de escolher um card (25/08/2026,
                        pedido do usuário) — antes dava pra clicar sem nada selecionado
                        e só descobrir pelo texto de erro. */}
                    <button onClick={handleConfirmar} disabled={!tipoHora}
                        className={`flex-1 py-3 rounded-full font-semibold text-[13px] transition-colors
                            ${tipoHora ? 'mes-btn-primary bg-petrol-600 hover:bg-petrol-500' : 'bg-surface-3 text-anthracite-500 cursor-not-allowed'}`}>
                        Confirmar
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModalMateriaisOP({ materiais, carregando, erro, onTentarNovamente, onConfirmar, onFechar, iniciando, projetoEspecial }) {
    useBloqueioScroll();
    const [modo, setModo] = useState('TOTAL');
    const [itensEditados, setItensEditados] = useState([]);
    useEffect(() => {
        setItensEditados(materiais.map((item) => ({
            ...item,
            quantidade_requisitar: Number(item.quantidade_pendente) || 0,
            lote: '',
        })));
    }, [materiais]);
    const semEmpenhos = !carregando && !erro && materiais.length === 0;
    const todosRequisitados = materiais.length > 0
        && materiais.every((item) => Number(item.quantidade_pendente) <= 0);
    const grupos = [
        { chave: 'materia_prima', titulo: 'Matérias-primas' },
        { chave: 'componente', titulo: 'Componentes' },
    ];
    const formatarQuantidade = (valor) => Number(valor || 0).toLocaleString('pt-BR', { maximumFractionDigits: 4 });

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-4xl p-7 animate-slide-up max-h-[90vh] flex flex-col">
                <div className="flex items-start justify-between gap-4 mb-5">
                    <div>
                        <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-2">
                            <Layers size={22} className="text-petrol-400" /> Materiais da OP
                        </h2>
                        <p className="text-[12px] text-anthracite-500 mt-1">
                            {todosRequisitados
                                ? 'Todos os materiais desta OP já foram requisitados. Não há quantidades pendentes.'
                                : 'Informe as quantidades e os lotes e escolha se a requisição será total ou parcial.'}
                        </p>
                    </div>
                    <button onClick={onFechar} disabled={iniciando} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500 disabled:opacity-40"><X size={18} /></button>
                </div>

                <div className="overflow-y-auto min-h-[180px] pr-1">
                    {carregando && <div className="h-40 flex items-center justify-center text-[13px] text-anthracite-400">Consultando empenhos...</div>}
                    {!carregando && erro && (
                        <div className="h-40 flex flex-col items-center justify-center gap-4 text-center">
                            <AlertTriangle size={28} className="text-amber-400" />
                            <p className="text-[13px] leading-relaxed text-anthracite-300 whitespace-pre-line">{erro}</p>
                            <button onClick={onTentarNovamente} className="mes-btn-ghost border border-white/[0.08] px-5 py-2.5">Tentar novamente</button>
                        </div>
                    )}
                    {semEmpenhos && (
                        <div className="h-40 flex flex-col items-center justify-center gap-3 px-6 text-center">
                            <AlertTriangle size={28} className="text-amber-400" />
                            <p className="text-[13px] font-semibold text-anthracite-300">Esta OP ainda não possui materiais empenhados.</p>
                            <p className="text-[12px] text-anthracite-500">Solicite ao PCP a geração dos empenhos antes de iniciar a produção.</p>
                        </div>
                    )}
                    {!carregando && !erro && !todosRequisitados && (
                        <div className="flex gap-2 mb-5">
                            {['TOTAL', 'PARCIAL'].map((opcao) => (
                                <button key={opcao} type="button" onClick={() => setModo(opcao)}
                                    className={`px-4 py-2 rounded-xl text-[12px] font-bold border transition-colors ${modo === opcao ? 'border-brand-500 bg-brand-500/15 text-brand-300' : 'border-white/[0.08] text-anthracite-400 hover:bg-white/[0.04]'}`}>
                                    Requisição {opcao === 'TOTAL' ? 'total' : 'parcial'}
                                </button>
                            ))}
                        </div>
                    )}
                    {!carregando && !erro && grupos.map((grupo) => {
                        const itens = itensEditados.filter((item) => item.categoria === grupo.chave);
                        if (itens.length === 0) return null;
                        return (
                            <section key={grupo.chave} className="mb-6 last:mb-0">
                                <h3 className="text-[12px] font-bold uppercase tracking-wider text-anthracite-400 mb-2">{grupo.titulo} ({itens.length})</h3>
                                <div className="overflow-x-auto rounded-xl border border-white/[0.06]">
                                    <table className="w-full text-left text-[12px]">
                                        <thead className="bg-white/[0.03] text-anthracite-500">
                                            <tr><th className="px-4 py-3">Código</th><th className="px-4 py-3">Descrição</th><th className="px-4 py-3">Armazém</th><th className="px-4 py-3 text-right">Pendente</th><th className="px-4 py-3">Quantidade</th><th className="px-4 py-3">Lote</th></tr>
                                        </thead>
                                        <tbody className="divide-y divide-white/[0.05]">
                                            {itens.map((item) => {
                                                const indice = itensEditados.findIndex((atual) => atual.codigo === item.codigo && atual.armazem === item.armazem);
                                                return (
                                                <tr key={`${item.codigo}-${item.armazem}`} className="text-anthracite-300">
                                                    <td className="px-4 py-3 font-semibold text-anthracite-200 whitespace-nowrap">{item.codigo}</td>
                                                    <td className="px-4 py-3">{item.descricao || '—'}</td>
                                                    <td className="px-4 py-3 whitespace-nowrap">{item.armazem || '—'}</td>
                                                    <td className="px-4 py-3 text-right tabular-nums">{formatarQuantidade(item.quantidade_pendente)}</td>
                                                    <td className="px-4 py-2"><input type="number" min="0" step="0.0001" disabled={Number(item.quantidade_pendente) <= 0} value={item.quantidade_requisitar} onChange={(e) => setItensEditados((lista) => lista.map((atual, i) => i === indice ? { ...atual, quantidade_requisitar: e.target.value } : atual))} className="mes-input w-28 py-2 text-right disabled:opacity-60" /></td>
                                                    <td className="px-4 py-2"><input type="text" maxLength={30} value={item.lote} onChange={(e) => setItensEditados((lista) => lista.map((atual, i) => i === indice ? { ...atual, lote: e.target.value } : atual))} disabled={Number(item.quantidade_pendente) <= 0} placeholder="Lote" className="mes-input w-32 py-2 disabled:opacity-60" /></td>
                                                </tr>
                                            );})}
                                        </tbody>
                                    </table>
                                </div>
                            </section>
                        );
                    })}
                </div>

                <div className="flex gap-3 mt-6 pt-5 border-t border-white/[0.06]">
                    <button onClick={onFechar} disabled={iniciando} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3 disabled:opacity-40">Cancelar</button>
                    <button onClick={() => onConfirmar({ modo, itens: itensEditados.filter((item) => Number(item.quantidade_requisitar) > 0).map((item) => ({ codigo: item.codigo, armazem: item.armazem, quantidade: Number(item.quantidade_requisitar), lote: item.lote })) })} disabled={carregando || !!erro || semEmpenhos || todosRequisitados || iniciando}
                        className="flex-1 mes-btn-primary bg-petrol-600 hover:bg-petrol-500 py-3 disabled:bg-surface-3 disabled:text-anthracite-500 disabled:cursor-not-allowed">
                        {iniciando ? 'Requisitando...' : 'Confirmar requisição'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ModalErroInicio({ mensagem, onConfirmar }) {
    useBloqueioScroll();
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/65 p-4">
            <div className="mes-modal w-full max-w-lg p-7 animate-slide-up text-center">
                <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-500/10 flex items-center justify-center">
                    <AlertTriangle size={26} className="text-red-400" />
                </div>
                <h2 className="text-xl font-bold text-anthracite-100">Não foi possível iniciar</h2>
                <p className="mt-3 text-[13px] leading-relaxed text-anthracite-300">{mensagem}</p>
                <button onClick={onConfirmar} autoFocus className="w-full mt-6 mes-btn-primary bg-petrol-600 hover:bg-petrol-500 py-3">
                    OK
                </button>
            </div>
        </div>
    );
}

function ModalEncerrarOP({ op, produzido, parcial, processando, erro, onConfirmar, onFechar }) {
    useBloqueioScroll();
    return (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/65 p-4">
            <div className="mes-modal w-full max-w-lg p-7 animate-slide-up">
                <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-2">
                        <Lock size={22} className="text-red-400" /> Encerrar Ordem de Produção
                    </h2>
                    {!processando && <button onClick={onFechar} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>}
                </div>
                <p className="mt-4 text-[13px] text-anthracite-300">
                    Confirma o encerramento definitivo da OP <strong>{op.codigo}</strong>?
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3 text-center">
                    <div className="rounded-xl bg-surface-3/50 p-3"><span className="block text-[11px] text-anthracite-500">Previsto</span><strong>{op.quantidadeAProduzir.toLocaleString('pt-BR')}</strong></div>
                    <div className="rounded-xl bg-surface-3/50 p-3"><span className="block text-[11px] text-anthracite-500">Produzido</span><strong>{produzido.toLocaleString('pt-BR')}</strong></div>
                </div>
                {parcial && <p className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/10 p-3 text-[12px] text-amber-300">Atenção: esta OP será encerrada parcialmente e não aceitará novos apontamentos.</p>}
                {erro && <p className="mt-4 rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-[12px] text-red-300 whitespace-pre-line">{erro}</p>}
                <div className="flex gap-3 mt-6">
                    <button onClick={onFechar} disabled={processando} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3 disabled:opacity-40">Cancelar</button>
                    <button onClick={onConfirmar} disabled={processando} className="flex-1 mes-btn-primary bg-red-600 hover:bg-red-500 py-3 disabled:opacity-50">{processando ? 'Encerrando...' : 'Encerrar OP'}</button>
                </div>
            </div>
        </div>
    );
}

// Confirmação de "Liberar Máquina" (25/08/2026, pedido do usuário) — antes
// fechava o setup na hora do clique, sem perguntar nada. Cancelar aqui é
// literalmente só fechar o modal (onFechar chama setModal(null) — nenhum
// outro estado muda, o cronômetro do setup continua rodando normalmente).
function ModalConfirmarLiberarMaquina({ onConfirmar, onFechar }) {
    useBloqueioScroll();
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-md p-7 animate-slide-up">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                        <CheckCircle size={22} className="text-petrol-400" /> Liberar máquina
                    </h2>
                    <button onClick={onFechar} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                </div>
                <p className="text-[13px] text-anthracite-400">
                    Tem certeza que o setup foi feito e a máquina está pronta pra uso?
                </p>
                <div className="flex gap-3 mt-6">
                    <button onClick={onFechar} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Cancelar</button>
                    <button onClick={onConfirmar} className="flex-1 mes-btn-primary bg-petrol-600 hover:bg-petrol-500 py-3">Confirmar</button>
                </div>
            </div>
        </div>
    );
}

function ModalPausa({ onConfirmar, onFechar, limiteFisico, limiteMeta, descontaRefugadoDoTeto = true }) {
    useBloqueioScroll();
    const [motivo, setMotivo] = useState('');
    const [obs, setObs] = useState('');
    const [prod, setProd] = useState('');
    const [refu, setRefu] = useState('');
    const [justificativa, setJustificativa] = useState('');
    const [erro, setErro] = useState('');
    const totalConsiderado = (prod !== '' && refu !== '')
        ? (descontaRefugadoDoTeto ? (parseInt(prod) + parseInt(refu)) : parseInt(prod))
        : null;
    // Excede a meta/quantidade da OP (21/08/2026, pedido do usuário) — mesma
    // regra de ModalQuantidades, ver lá.
    const excedeMeta = limiteMeta != null && totalConsiderado != null && totalConsiderado > limiteMeta;

    const handleConfirmar = () => {
        if (!motivo) { setErro('Informe o motivo da parada.'); return; }
        if (prod === '' || refu === '') { setErro('Informe as peças produzidas e refugadas.'); return; }
        // Teto FÍSICO (13/08/2026, estendido 14/08/2026): não dá pra produzir
        // mais do que a operação anterior do roteiro já entregou — mesmo lote
        // físico, continua bloqueio duro sem exceção (ver ModalQuantidades).
        // Refugada TAMBÉM sai do mesmo total (bug real 13/08/2026: só somava
        // produzida) quando existe operação anterior (lote físico recebido
        // dela). Sem operação anterior (1ª do roteiro), a meta é um alvo de
        // peça boa, não um orçamento de tentativas (bug real 18/08/2026) — só
        // a produzida conta contra o teto, `descontaRefugadoDoTeto` reflete isso.
        if (limiteFisico != null && totalConsiderado > limiteFisico) {
            setErro(`Quantidade produzida + refugada (${totalConsiderado}) maior que o restante disponível nesta operação (${limiteFisico} peça(s)).`);
            return;
        }
        if (excedeMeta && !justificativa.trim()) {
            setErro('Quantidade acima da meta desta operação — informe a justificativa abaixo pra continuar.');
            return;
        }
        onConfirmar({ motivo, obs, produzido: parseInt(prod), refugado: parseInt(refu), justificativa: excedeMeta ? justificativa.trim() : undefined });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-xl p-7 animate-slide-up">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-anthracite-100 flex items-center gap-2">
                        <Pause size={22} className="text-amber-400" /> Pausar Produção
                    </h2>
                    <button onClick={onFechar} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-[11px] font-semibold text-anthracite-400 uppercase tracking-wider mb-1.5">Motivo da Parada *</label>
                        <select value={motivo} onChange={e => setMotivo(e.target.value)} className="mes-input">
                            <option value="">Selecione o motivo...</option>
                            {MOTIVOS_PARADA.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-[11px] font-semibold text-anthracite-400 uppercase tracking-wider mb-1.5">Peças Produzidas *</label>
                            <input type="number" min="0" value={prod} onChange={e => setProd(e.target.value)} className="mes-input" placeholder="0" />
                        </div>
                        <div>
                            <label className="block text-[11px] font-semibold text-anthracite-400 uppercase tracking-wider mb-1.5">Refugos *</label>
                            <input type="number" min="0" value={refu} onChange={e => setRefu(e.target.value)} className="mes-input" placeholder="0" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-anthracite-400 uppercase tracking-wider mb-1.5">Observação</label>
                        <textarea value={obs} onChange={e => setObs(e.target.value)} rows={3} className="mes-input resize-none" placeholder="Detalhes..." />
                    </div>
                    {excedeMeta && (
                        <div>
                            <label className="block text-[11px] font-semibold text-amber-400 uppercase tracking-wider mb-1.5">
                                Quantidade acima da meta desta operação ({limiteMeta} peça(s)) — Justificativa *
                            </label>
                            <textarea value={justificativa} onChange={e => setJustificativa(e.target.value)} rows={3} className="mes-input resize-none" placeholder="Explique o motivo do excesso..." />
                        </div>
                    )}
                    {erro && <p className="text-red-400 text-[12px]">{erro}</p>}
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onFechar} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Cancelar</button>
                    <button onClick={handleConfirmar} className="flex-1 mes-btn-primary bg-amber-600 hover:bg-amber-500 py-3">Confirmar Pausa</button>
                </div>
            </div>
        </div>
    );
}

function ModalQuantidades({ onConfirmar, onFechar, limiteFisico, limiteMeta, descontaRefugadoDoTeto = true }) {
    useBloqueioScroll();
    const [prod, setProd] = useState('');
    const [refu, setRefu] = useState('');
    const [justificativa, setJustificativa] = useState('');
    const [erro, setErro] = useState('');
    const totalConsiderado = (prod !== '' && refu !== '')
        ? (descontaRefugadoDoTeto ? (parseInt(prod) + parseInt(refu)) : parseInt(prod))
        : null;
    // Excede a META/quantidade da OP (21/08/2026, pedido do usuário: deixou
    // de bloquear, passa a exigir justificativa) — mostra o campo assim que
    // dá pra calcular o total, mesmo antes de clicar em confirmar, pro
    // operador já ver que vai precisar justificar.
    const excedeMeta = limiteMeta != null && totalConsiderado != null && totalConsiderado > limiteMeta;
    const handleConfirmar = () => {
        if (prod === '' || refu === '') { setErro('Preencha as quantidades.'); return; }
        // Teto FÍSICO (o que a operação anterior do roteiro entregou) — este
        // continua bloqueio duro, sem exceção nenhuma: não dá pra "inventar"
        // peça que a etapa anterior não produziu de verdade (pedido do
        // usuário 21/08/2026, ao liberar só o teto de meta abaixo).
        if (limiteFisico != null && totalConsiderado > limiteFisico) {
            setErro(`Quantidade produzida + refugada (${totalConsiderado}) maior que o restante disponível nesta operação (${limiteFisico} peça(s)).`);
            return;
        }
        if (excedeMeta && !justificativa.trim()) {
            setErro('Quantidade acima da meta desta operação — informe a justificativa abaixo pra continuar.');
            return;
        }
        onConfirmar({ produzido: parseInt(prod), refugado: parseInt(refu), justificativa: excedeMeta ? justificativa.trim() : undefined });
    };
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-lg p-7 animate-slide-up">
                <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-bold text-anthracite-100">Apontar Produção</h2>
                    <button onClick={onFechar} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                </div>
                <div className="space-y-4">
                    <div>
                        <label className="block text-[11px] font-semibold text-anthracite-400 uppercase tracking-wider mb-1.5">Peças Produzidas *</label>
                        <input type="number" min="0" value={prod} onChange={e => setProd(e.target.value)} className="mes-input" placeholder="0" />
                    </div>
                    <div>
                        <label className="block text-[11px] font-semibold text-anthracite-400 uppercase tracking-wider mb-1.5">Peças Refugadas *</label>
                        <input type="number" min="0" value={refu} onChange={e => setRefu(e.target.value)} className="mes-input" placeholder="0" />
                    </div>
                    {excedeMeta && (
                        <div>
                            <label className="block text-[11px] font-semibold text-amber-400 uppercase tracking-wider mb-1.5">
                                Quantidade acima da meta desta operação ({limiteMeta} peça(s)) — Justificativa *
                            </label>
                            <textarea value={justificativa} onChange={e => setJustificativa(e.target.value)} rows={3} className="mes-input resize-none" placeholder="Explique o motivo do excesso..." />
                        </div>
                    )}
                    {erro && <p className="text-red-400 text-[12px]">{erro}</p>}
                </div>
                <div className="flex gap-3 mt-6">
                    <button onClick={onFechar} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Cancelar</button>
                    <button onClick={handleConfirmar} className="flex-1 mes-btn-primary bg-petrol-600 hover:bg-petrol-500 py-3">Gerar Resumo</button>
                </div>
            </div>
        </div>
    );
}

function ModalResumo({ dados, onConfirmar, onFechar }) {
    useBloqueioScroll();
    const totalProduzido = dados.produzido + dados.refugado;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-2xl overflow-hidden animate-slide-up">
                <div className="bg-surface-3 p-7">
                    <div className="flex items-center gap-3 mb-1">
                        <ClipboardList className="text-petrol-400" size={26} />
                        <h2 className="text-xl font-bold text-anthracite-50">Resumo do Apontamento</h2>
                    </div>
                    <p className="text-anthracite-500 text-[12px]">Confira antes de concluir.</p>
                </div>
                <div className="p-7 space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                        {[{ label: 'Operador', value: dados.operador }, { label: 'Máquina', value: dados.maquina }, { label: 'Produto', value: dados.produto }, { label: 'OP', value: dados.op }, { label: 'Operação', value: dados.operacaoLabel || '—' }].map((item, i) => (
                            <div key={i}>
                                <span className="block text-[10px] text-anthracite-500 uppercase mb-1">{item.label}</span>
                                <span className="text-[13px] font-semibold text-anthracite-100">{item.value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mes-divider" />
                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: 'Produzidas', value: dados.produzido, color: 'text-petrol-400' },
                            { label: 'Refugadas', value: dados.refugado, color: 'text-red-400' },
                            { label: 'Total', value: totalProduzido, color: 'text-brand-400' },
                        ].map((item, i) => (
                            <div key={i} className="bg-surface-3/30 rounded-xl p-4 text-center border border-white/[0.03]">
                                <span className="block text-[10px] text-anthracite-500 uppercase mb-1">{item.label}</span>
                                <span className={`text-2xl font-bold ${item.color} tabular-nums`}>{item.value}</span>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 bg-surface-3/30 rounded-xl p-4 text-[12px] font-medium text-anthracite-400 border border-white/[0.03]">
                            <Clock size={14} className="text-anthracite-500" /> Início: {dados.inicio}
                        </div>
                        <div className="flex items-center gap-2 bg-surface-3/30 rounded-xl p-4 text-[12px] font-medium text-anthracite-400 border border-white/[0.03]">
                            <Pause size={14} className="text-anthracite-500" /> Paradas: {dados.paradas}
                        </div>
                    </div>
                    {dados.justificativa && (
                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                            <span className="block text-[10px] text-amber-400 uppercase mb-1 font-semibold">Justificativa (quantidade acima da meta)</span>
                            <span className="text-[13px] text-anthracite-200">{dados.justificativa}</span>
                        </div>
                    )}
                </div>
                <div className="p-7 border-t border-white/[0.04] flex gap-3">
                    <button onClick={onFechar} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Voltar</button>
                    <button onClick={onConfirmar} className="flex-[2] mes-btn-primary bg-petrol-600 hover:bg-petrol-500 py-3">Concluir Apontamento</button>
                </div>
            </div>
        </div>
    );
}

function ModalHistorico({ op, historico, operacaoAtual, totalProduzido, totalRefugado, onFechar }) {
    useBloqueioScroll();
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
            <div className="mes-modal w-full max-w-3xl max-h-[85vh] flex flex-col overflow-hidden animate-slide-up">
                <div className="p-6 border-b border-white/[0.04] flex items-start justify-between">
                    <div>
                        <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                            <History size={20} className="text-brand-400" /> Histórico de Apontamentos
                        </h2>
                        <p className="text-[12px] text-anthracite-500 mt-1">Ordem {op.codigo} · {op.produtoNome}</p>
                    </div>
                    <button onClick={onFechar} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                </div>
                <div className="flex-1 overflow-y-auto p-6">
                    {historico.length === 0 ? (
                        <div className="text-center py-10 text-anthracite-500">
                            <History size={32} className="mx-auto mb-2 text-anthracite-600" />
                            <p className="text-[13px]">Nenhum apontamento registrado ainda.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {historico.map(r => (
                                <div key={r.id} className="bg-surface-3/30 rounded-xl border border-white/[0.03] p-4 flex items-center justify-between gap-4">
                                    <div className="min-w-0">
                                        <p className="text-[13px] font-semibold text-anthracite-200 truncate">
                                            {r.data}
                                            {/* Operação (13/08/2026): esse histórico mistura TODAS as operações
                                                do roteiro (de propósito, é o que decide a liberação em cascata) —
                                                sem esse rótulo não dava pra saber qual apontamento é de qual etapa. */}
                                            {r.operacao && <span className="ml-2 text-[10px] font-bold text-brand-400 uppercase">Operação {r.operacao}</span>}
                                            {/* Tipo de Hora (21/08/2026) — resolve o código (ZH6_TPHORA) pro
                                                rótulo aqui na exibição, mesma lista TIPOS_HORA do ModalTipoHora. */}
                                            {r.tipoHora && (
                                                <span className="ml-2 text-[10px] font-bold text-petrol-400 uppercase">
                                                    {TIPOS_HORA.find(t => t.codigo === r.tipoHora)?.label || r.tipoHora}
                                                </span>
                                            )}
                                        </p>
                                        <p className="text-[11px] text-anthracite-500 mt-0.5">
                                            Operador: {r.operador} · Início {r.inicio || '--:--'} · {r.fim ? `Fim ${r.fim}` : 'Em andamento'} · Duração {r.tempo}
                                        </p>
                                        {r.motivo && <p className="text-[11px] text-amber-400 font-medium mt-0.5">Parada: {r.motivo}</p>}
                                        {r.justificativa && <p className="text-[11px] text-amber-400 font-medium mt-0.5">Excesso justificado: {r.justificativa}</p>}
                                    </div>
                                    <div className="flex gap-4 text-right shrink-0">
                                        <div>
                                            <span className="block text-[10px] text-anthracite-500 uppercase">Produzido</span>
                                            <span className="text-[13px] font-bold text-petrol-400">{r.produzido}</span>
                                        </div>
                                        <div>
                                            <span className="block text-[10px] text-anthracite-500 uppercase">Refugo</span>
                                            <span className="text-[13px] font-bold text-red-400">{r.refugado}</span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
                <div className="p-6 border-t border-white/[0.04]">
                    {/* Total (13/08 → corrigido 18/08/2026): esta lista mistura de propósito
                        apontamentos de TODAS as operações do roteiro (ver comentário acima), mas
                        somar produzido/refugado entre operações diferentes não faz sentido físico
                        (cada uma processa o mesmo lote em sua própria etapa, não é aditivo). O total
                        abaixo usa os mesmos valores já escopados à operação ATUAL que alimentam os
                        cards do topo da tela (produzidoAcumulado/refugadoAcumulado), nunca a soma
                        bruta da lista. */}
                    {operacaoAtual && (
                        <p className="text-[11px] text-anthracite-500 mb-3 text-center">Total da Operação {operacaoAtual}</p>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="bg-surface-3/30 rounded-xl border border-white/[0.03] p-4 text-center">
                            <span className="block text-[10px] text-anthracite-500 uppercase mb-1">Total Produzido</span>
                            <span className="text-xl font-bold text-petrol-400 tabular-nums">{totalProduzido}</span>
                        </div>
                        <div className="bg-surface-3/30 rounded-xl border border-white/[0.03] p-4 text-center">
                            <span className="block text-[10px] text-anthracite-500 uppercase mb-1">Total Refugado</span>
                            <span className="text-xl font-bold text-red-400 tabular-nums">{totalRefugado}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Documentação (25/08/2026, pedido do usuário) — botão novo ao lado de
// Histórico, pra outros documentos da OP/produto além de PFI/Imagem/Desenho
// (que já têm aba própria no MediaPanel).
//
// Ligado a uma pasta real do servidor em 26/08/2026 (pedido do usuário) —
// uma subpasta por código de produto, com todos os PDFs dela (ver
// services/documentosProdutos.js/api/producao/documentos/:produtoCodigo).
// Diferente de PFI/Imagem/Desenho (nome de arquivo fixo esperado), aqui a
// lista vem dinâmica da pasta — não precisa saber a nomenclatura de
// antemão, só filtra por .pdf.
function ModalDocumentacao({ op, documentos, carregando, onFechar }) {
    useBloqueioScroll();
    // Visualização maximizada (27/08/2026, pedido do usuário: "mesmo padrão"
    // do lightbox já usado em PFI/Imagem/Desenho, ver MediaPanel) — abre em
    // tela cheia por cima de tudo (portal pro body), só com um X pra fechar
    // e voltar pra lista, em vez de um iframe pequeno dentro do próprio
    // modal. A lista continua aberta por baixo, igual o painel de mídia
    // continua por baixo do lightbox de Imagem/Desenho.
    const [docAberto, setDocAberto] = useState(null); // { nome, url } | null
    return (
        <>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                <div className="mes-modal w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-slide-up">
                    <div className="p-6 border-b border-white/[0.04] flex items-start justify-between shrink-0">
                        <div>
                            <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                                <FolderOpen size={20} className="text-brand-400" /> Documentação
                            </h2>
                            <p className="text-[12px] text-anthracite-500 mt-1">Ordem {op.codigo} · {op.produtoNome}</p>
                        </div>
                        <button onClick={onFechar} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6">
                        {carregando ? (
                            <p className="text-[13px] text-anthracite-500 text-center py-10">Carregando documentos...</p>
                        ) : (!documentos || documentos.length === 0) ? (
                            <div className="text-center py-10 text-anthracite-500">
                                <FolderOpen size={32} className="mx-auto mb-2 text-anthracite-600" />
                                <p className="text-[13px]">Nenhum documento disponível ainda.</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {documentos.map((doc) => (
                                    <button key={doc.url} type="button" onClick={() => setDocAberto(doc)}
                                        className="w-full flex items-center gap-3 bg-surface-3/30 rounded-xl border border-white/[0.03] p-4 hover:border-brand-500/40 transition-colors text-left">
                                        <FileText size={18} className="text-anthracite-500 shrink-0" />
                                        <span className="text-[13px] font-medium text-anthracite-200 truncate">{doc.nome}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {docAberto && createPortal(
                <div onClick={() => setDocAberto(null)}
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-6">
                    <iframe src={docAberto.url} title={docAberto.nome} onClick={(e) => e.stopPropagation()} className="w-full h-full bg-white rounded-xl" />
                    <button onClick={(e) => { e.stopPropagation(); setDocAberto(null); }}
                        className="absolute top-4 right-4 p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors">
                        <X size={18} />
                    </button>
                </div>,
                document.body
            )}
        </>
    );
}

const STAT_ACCENTS = {
    gray: 'bg-surface-3/50 text-anthracite-400',
    green: 'bg-petrol-500/10 text-petrol-400',
    blue: 'bg-brand-500/10 text-brand-400',
    red: 'bg-red-500/10 text-red-400',
};

function BigStat({ label, value, icon: Icon, accent = 'gray', mono = false, highlight = false, compact = false }) {
    const displayValue = typeof value === 'number' ? value.toLocaleString('pt-BR') : value;
    const valueCls = `font-bold tabular-nums leading-tight ${mono ? 'font-mono' : ''} ${highlight ? 'text-petrol-400' : 'text-anthracite-100'}`;

    if (compact) {
        return (
            <div className="h-full mes-card rounded-xl flex flex-col items-center justify-center text-center gap-1 p-2 min-w-0">
                <span className="text-[10px] text-anthracite-500 uppercase">{label}</span>
                <span className={`${valueCls} truncate max-w-full text-sm lg:text-base`}>{displayValue}</span>
            </div>
        );
    }
    return (
        <div className="h-full mes-card rounded-xl p-3 flex items-center justify-center gap-3 min-w-0">
            {/* mt-1.5 (19/08/2026, achado do usuário: ícone parecia
                "descentralizado") — geometricamente já estava certinho
                (`items-center` alinha os centros dos 2 blocos com precisão,
                confirmado medindo), mas visualmente o número grande/negrito
                pesa mais que o rótulo pequeno acima dele, puxando o "centro
                de peso" percebido do bloco de texto pra baixo do centro
                geométrico — um nudge pequeno no ícone compensa essa ilusão
                de ótica, sem quebrar o alinhamento nos outros breakpoints. */}
            <div className={`p-2.5 rounded-xl shrink-0 mt-1.5 ${STAT_ACCENTS[accent]}`}>
                <Icon size={20} />
            </div>
            <div className="min-w-0">
                <span className="block text-[10px] text-anthracite-500 uppercase truncate">{label}</span>
                <span className={`${valueCls} block truncate text-lg lg:text-2xl`}>{displayValue}</span>
            </div>
        </div>
    );
}

// Isolado do resto da tela (12/08/2026, investigação de performance) — antes
// o cronômetro vivia como estado (`elapsed`) no componente Producao inteiro,
// então TODA a árvore (histórico, MediaPanel, modais etc.) re-renderizava a
// cada segundo, o turno inteiro, pra todo operador. Agora só este componente
// pequeno re-renderiza por segundo; o valor corrente é espelhado num useRef
// no Producao (via onTick), pra qualquer handler (Pausar/Apontar/Iniciar)
// sempre conseguir ler o valor mais atual sem precisar de estado no
// componente pai. `key` no <Timer> (ver uso mais abaixo) força remontar com
// um novo initialValue sempre que o pai precisa "resetar" o cronômetro (nova
// OP, iniciar do zero, pausar, concluir apontamento) — mais simples e mais
// seguro que sincronizar isso via effect.
function Timer({ running, initialValue, onTick }) {
    const [valor, setValor] = useState(initialValue);
    const intervalRef = useRef(null);

    useEffect(() => {
        if (running) {
            intervalRef.current = setInterval(() => {
                setValor((v) => {
                    const proximo = v + 1;
                    onTick(proximo);
                    return proximo;
                });
            }, 1000);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [running]);

    return <BigStat label="Tempo" value={formatTime(valor)} icon={Clock} mono highlight={running} compact />;
}

function isPdf(value) {
    return typeof value === 'string' && value.toLowerCase().endsWith('.pdf');
}

// Upload/"Trocar" removidos (27/08/2026, pedido do usuário) — PFI/Imagem/
// Desenho agora vêm de verdade da pasta real de documentação no servidor
// (ver services/documentosProdutos.js), somente leitura; não faz mais
// sentido oferecer "anexar"/"trocar" pelo Persona (o endpoint de upload já
// era só um placeholder desativado desde 04/08/2026, decisão original de
// escopo — ver uploadMidia/producao.js, também removido).
function MediaPanel({ pfiPeca, imagemPeca, desenhoPeca, produtoCodigo, produtoDescricao }) {
    const [tab, setTab] = useState('imagem');
    const [lightboxAberto, setLightboxAberto] = useState(false);
    const imagemReal = typeof imagemPeca === 'string' && imagemPeca.length > 0;
    const desenhoReal = typeof desenhoPeca === 'string' && desenhoPeca.length > 0;
    const arquivo = tab === 'imagem' ? imagemPeca : desenhoPeca;
    const podeAmpliar = tab === 'imagem' ? imagemReal : desenhoReal;
    return (
        <div className="h-full flex flex-col">
            <div className="flex gap-1 bg-surface-3/50 rounded-xl p-1 mb-3 shrink-0">
                <button onClick={() => setTab('imagem')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${tab === 'imagem' ? 'bg-surface-2 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300'}`}>
                    <ImageIcon size={13} /> Imagem
                </button>
                <button onClick={() => setTab('desenho')}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${tab === 'desenho' ? 'bg-surface-2 text-brand-400 shadow-sm' : 'text-anthracite-500 hover:text-anthracite-300'}`}>
                    <FileText size={13} /> Desenho
                </button>
            </div>
            <div onClick={() => podeAmpliar && setLightboxAberto(true)}
                className={`flex-1 min-h-0 bg-surface-3/30 rounded-xl border overflow-hidden relative transition-colors ${podeAmpliar ? 'border-dashed border-white/[0.06] cursor-pointer hover:border-brand-500/40 group flex items-center justify-center' : 'border-white/[0.06]'}`}>
                {tab === 'imagem' ? (
                    imagemReal ? (
                        <>
                            {isPdf(arquivo) ? (
                                <iframe src={arquivo} title="imagem" className="w-full h-full pointer-events-none" />
                            ) : (
                                <img src={arquivo} alt="imagem" className="object-contain w-full h-full p-2" />
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none">
                                <Maximize2 size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        </>
                    ) : (
                        <Vista3DProduto codigo={produtoCodigo} descricao={produtoDescricao} />
                    )
                ) : desenhoReal ? (
                    <>
                        {isPdf(arquivo) ? (
                            <iframe src={arquivo} title="desenho" className="w-full h-full pointer-events-none" />
                        ) : (
                            <img src={arquivo} alt="desenho" className="object-contain w-full h-full p-2" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors pointer-events-none">
                            <Maximize2 size={22} className="text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                    </>
                ) : (
                    <DesenhoTecnicoProduto codigo={produtoCodigo} descricao={produtoDescricao} />
                )}
            </div>

            {lightboxAberto && podeAmpliar && createPortal(
                <div onClick={() => setLightboxAberto(false)}
                    className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 p-6">
                    {isPdf(arquivo) ? (
                        <iframe src={arquivo} title={tab} onClick={(e) => e.stopPropagation()} className="w-full h-full bg-white rounded-xl" />
                    ) : (
                        <img src={arquivo} alt={tab} onClick={(e) => e.stopPropagation()} className="max-w-full max-h-full object-contain rounded-xl" />
                    )}
                    <button onClick={(e) => { e.stopPropagation(); setLightboxAberto(false); }}
                        className="absolute top-4 right-4 p-2 rounded-xl bg-white/10 text-white hover:bg-white/20 transition-colors">
                        <X size={18} />
                    </button>
                </div>,
                document.body
            )}
        </div>
    );
}

const Producao = () => {
    const { operadorLogado, definirMaquina, limparMaquina, atualizarMaquinasDisponiveis, logout, temPermissao } = useAuthStore();
    // Preparador (25/08/2026, pedido do usuário) — permissão do funcionário,
    // não perfil novo: nem todo operador pode fazer setup, mas todo
    // Preparador pode operar normal. Admin já passa livre (temPermissao
    // sempre true pra admin, ver useAuthStore). Usada só pra travar o
    // ModalTipoHora quando falta setup e o operador não tem essa permissão.
    const podePreparar = temPermissao('preparador');
    const navigate = useNavigate();
    const handleLogout = () => { logout(); navigate('/login'); };
    const [op, setOp] = useState(null);
    // OP "em preview" — clicada na lista, mas ainda não confirmada (ver
    // handlePreVisualizarOP). É o que faz o painel de sequência aparecer do
    // lado direito na mesma tela, antes mesmo de confirmar a seleção.
    const [opPreview, setOpPreview] = useState(null);
    const [status, setStatus] = useState('AGUARDANDO');
    const [iniciando, setIniciando] = useState(false);
    // elapsedRef (não estado) + timerResetKey — ver componente Timer acima.
    // resetElapsed() é o substituto direto de "setElapsed(x)": atualiza o
    // valor que os handlers leem (elapsedRef.current) e força o <Timer> a
    // remontar já mostrando esse valor nesse mesmo instante (key), sem
    // esperar o próximo tick.
    const elapsedRef = useRef(0);
    const [timerResetKey, setTimerResetKey] = useState(0);
    const resetElapsed = (valor) => {
        elapsedRef.current = valor;
        setTimerResetKey((k) => k + 1);
    };
    const [running, setRunning] = useState(false);
    const [horaInicio, setHoraInicio] = useState('--:--');
    const [produzidoAcumulado, setProduzidoAcumulado] = useState(0);
    const [refugadoAcumulado, setRefugadoAcumulado] = useState(0);
    // Produzido de CADA operação do roteiro (13/08/2026, mapa { operacao: qtd }
    // vindo de ordem.produzido_por_operacao) — usado só pra achar quanto a
    // operação ANTERIOR à escolhida já entregou (teto físico do que dá pra
    // apontar aqui). produzidoAcumulado acima já é só da operação ATUAL.
    const [produzidoPorOperacao, setProduzidoPorOperacao] = useState({});
    const [paradasCount, setParadasCount] = useState(0);
    const [historico, setHistorico] = useState([]);
    const [imagemPeca, setImagemPeca] = useState(null);
    const [desenhoPeca, setDesenhoPeca] = useState(null);
    // PFI (Plano de Fabricação, 25/08/2026, pedido do usuário) — 3ª mídia da
    // peça, mesmo padrão de imagem/desenho (ver services/midiaProdutos.js).
    // Vira aba PRINCIPAL do MediaPanel (a primeira, selecionada por padrão).
    const [pfiPeca, setPfiPeca] = useState(null);
    // Documentação técnica por produto (26/08/2026, pedido do usuário) —
    // buscada sob demanda só quando o modal abre (ver handleAbrirDocumentacao
    // mais abaixo), não junto do resto da sessão: é leitura de pasta de rede
    // no servidor, custo desnecessário se o operador nunca clicar em
    // "Documentação" naquele apontamento.
    const [documentosOP, setDocumentosOP] = useState([]);
    const [carregandoDocumentos, setCarregandoDocumentos] = useState(false);
    const [modal, setModal] = useState('selecao');
    const [materiaisOP, setMateriaisOP] = useState([]);
    const [carregandoMateriais, setCarregandoMateriais] = useState(false);
    const [erroMateriais, setErroMateriais] = useState('');
    const [requisicaoDuranteApontamento, setRequisicaoDuranteApontamento] = useState(false);
    // Tipo de Hora (21/08/2026) — tipoHoraSessao guarda a escolha feita no
    // ModalTipoHora até o clique seguinte em "Iniciar Produção" consumir ela
    // (ver handleIniciarClick/executarInicio); depois disso volta pra vazio,
    // pra pedir de novo no próximo ciclo. tipoHoraAtiva guarda o tipo do
    // ciclo que ACABOU de rodar (só muda quando executarInicio roda de
    // verdade) — usado só pra saber, na hora de Pausar/Apontar, se aquele
    // ciclo que está fechando era de Preparação (ver setup obrigatório
    // abaixo). setupFeito vem do backend (ordem.setup_feito, ver obterSessao)
    // e é atualizado localmente também assim que um ciclo de Preparação
    // fecha, sem esperar o próximo refresh da tela.
    const [tipoHoraSessao, setTipoHoraSessao] = useState('');
    const [tipoHoraAtiva, setTipoHoraAtiva] = useState('');
    const [setupFeito, setSetupFeito] = useState(false);
    // Projeto Especial (29/08/2026, pedido do usuário) — OP marcada com
    // SC2.C2_ZESPECI='2' (ver obterSessao no backend). Depois do setup feito,
    // a produção dessa OP é sempre código 4 (Produção Especial) — nunca uma
    // escolha manual do operador, então pula o ModalTipoHora inteiro (ver
    // handleIniciarClick). O destaque amarelo no cabeçalho usa este mesmo
    // valor.
    const [especial, setEspecial] = useState(false);
    const [dadosResumo, setDadosResumo] = useState(null);
    const [encerrandoOP, setEncerrandoOP] = useState(false);
    const [erroEncerramento, setErroEncerramento] = useState('');
    // carregandoSessao (25/08/2026, mesmo problema de fundo dos 2 fixes
    // anteriores nesta função) — enquanto true, a tela some com o aviso
    // contextual e os botões de ação (ver "Main"/"Actions" mais abaixo).
    // Sem isso, o primeiro render depois de escolher uma OP acontecia com
    // produzidoAcumulado/refugadoAcumulado/produzidoPorOperacao/setupFeito
    // já zerados (reset síncrono no início de handleSelecionarOP, antes do
    // fetch responder) — dava pra ver, por um instante, um aviso vermelho
    // errado ("já apontado o total, não é possível apontar mais") e os
    // botões trocados pra "Iniciar Setup" (setupFeito=false força isso),
    // mesmo quando a OP de verdade já tinha saldo/setup feito.
    const [carregandoSessao, setCarregandoSessao] = useState(false);
    const [ordensDisponiveis, setOrdensDisponiveis] = useState([]);
    const [carregandoOrdens, setCarregandoOrdens] = useState(true);
    // Verificação de "OP em andamento nessa máquina" (06/08/2026, decisão do
    // usuário) — true até saber se dá pra pular direto pra uma OP aberta ou
    // se precisa mostrar a telinha normal de escolher OP (ver useEffect logo
    // abaixo de handleSelecionarOP). Começa true pra não flashar a telinha de
    // escolha antes dessa checagem terminar.
    const [verificandoEmAndamento, setVerificandoEmAndamento] = useState(true);
    // OP em andamento detectada nesta máquina (ver useEffect logo abaixo de
    // handleSelecionarOP) — guarda {codigo, ordem, apontamentos} já buscados,
    // só pra mostrar o aviso; só vira a OP atual (setOp) quando o operador
    // clica OK (ver aplicarSessaoComoOpAtual/ModalOpEmAndamento), nunca sozinho.
    const [opEmAndamento, setOpEmAndamento] = useState(null);
    const [operacoesRoteiro, setOperacoesRoteiro] = useState([]);
    const [carregandoOperacoes, setCarregandoOperacoes] = useState(false);
    // Produzido real (ZH6+SH6, já combinado pelo backend) de cada operação da
    // OP em pré-visualização — usado só pra tirar do "Qual sequência você vai
    // fazer?" uma operação que já bateu a quantidade mas nunca foi totalizada
    // no Protheus (13/08/2026, achado real: usuário selecionava uma sequência
    // já 100% produzida fora do Persona sem nenhum aviso — a liberação em cascata
    // só olhava "a anterior teve apontamento", nunca "esta aqui já terminou").
    const [produzidoPorOperacaoPreview, setProduzidoPorOperacaoPreview] = useState({});
    // Meta por operação (14/08/2026) — mesma origem/timing de
    // produzidoPorOperacaoPreview (sessao.ordem.meta_por_operacao), usada por
    // candidatosSequencia pra não filtrar fora a 1ª operação já "reaberta"
    // por refugo posterior (senão a OP reaparece na lista, graças ao fix do
    // backend, mas some de novo um nível mais fundo ao clicar nela).
    const [metaPorOperacaoPreview, setMetaPorOperacaoPreview] = useState({});
    // Reatribuição de máquina (25/08/2026) — { [operacao]: recursoNovo },
    // mesma origem/timing de produzidoPorOperacaoPreview (sessao.ordem).
    // Sem isso, candidatosMaquina abaixo só enxergava a G2_RECURSO crua do
    // roteiro — uma operação reatribuída pelo PCP pra esta máquina não
    // aparecia como opção de sequência pra escolher, mesmo já aparecendo
    // certo na lista de OPs (esse fix é só no backend, ver
    // operacoesPendentesNaMaquina em producao.js).
    const [reatribuicoesPorOperacaoPreview, setReatribuicoesPorOperacaoPreview] = useState({});
    // Só usado quando a máquina do login participa de mais de uma sequência do
    // mesmo roteiro (ver candidatosSequencia abaixo) — operador escolhe qual.
    const [sequenciaEscolhida, setSequenciaEscolhida] = useState(null);
    const [erroApi, setErroApi] = useState('');
    const [erroInicioModal, setErroInicioModal] = useState('');
    // Busca ao vivo da lista de máquinas do usuário (17/09/2026, novo fluxo) —
    // controla o spinner da telinha de escolha de máquina (ModalSelecionarMaquinaLogin)
    // enquanto o operador que não veio com máquina resolvida no login ainda está
    // vendo as candidatas. Sem isso, quem caísse nesse caso podia ficar preso no
    // aviso "Seu usuário não tem nenhuma máquina liberada." sem listar nada.
    const [carregandoMaquinas, setCarregandoMaquinas] = useState(false);
    // Toast (18/08/2026, pedido do usuário: menos blocos coloridos empilhados
    // no topo da tela) — some sozinho depois de um tempo, sem precisar clicar
    // no X; qualquer erro novo reinicia a contagem.
    useEffect(() => {
        if (!erroApi) return;
        // Duração proporcional ao tamanho da mensagem (01/09/2026) — mensagens
        // mais longas (ex.: falha na requisição de materiais no Protheus,
        // que já vem com o motivo real + eventual aviso de estado
        // indeterminado) precisam de mais tempo pra ler do que um erro curto
        // genérico. 6s continua sendo o piso, igual antes.
        const t = setTimeout(() => setErroApi(''), Math.max(6000, erroApi.length * 80));
        return () => clearTimeout(t);
    }, [erroApi]);
    const progresso = op ? Math.min((produzidoAcumulado / op.quantidadeAProduzir) * 100, 100) : 0;

    // A OP segue um roteiro (SG2.G2_CODIGO), que tem várias sequências
    // (G2_OPERAC) — cada sequência tem uma máquina fixa (G2_RECURSO), mas o
    // INVERSO não é verdade: confirmado com dado real (04/08/2026) que a mesma
    // máquina pode aparecer em várias sequências do mesmo roteiro (1449 casos
    // reais só na amostra local — ex. produto M3200010, roteiro 01, máquina
    // 056022 faz as sequências 01, 02 e 03). Por isso a máquina do login (ver
    // Login.jsx/useAuthStore) só FILTRA candidatas — o operador sempre confirma
    // qual das sequências liberadas daquela máquina (painel dentro do próprio
    // ModalSelecionarOP), nunca escolhe entre o roteiro inteiro.
    const recursoSessao = (operadorLogado?.maquina || '').trim();
    const recursoNomeSessao = operadorLogado?.maquinaNome || (recursoSessao ? `Recurso ${recursoSessao}` : '');
    // Código antes do nome (19/08/2026, pedido do usuário, em todo lugar que
    // a máquina aparece) — só combina quando os dois existem de verdade; sem
    // nome resolvido, `recursoNomeSessao` já cai no fallback "Recurso
    // {código}" sozinho, combinar de novo geraria duplicata.
    const recursoCodigoNomeSessao = recursoSessao && operadorLogado?.maquinaNome
        ? `${recursoSessao} - ${operadorLogado.maquinaNome}`
        : recursoNomeSessao;

    // Liberação em cascata (04/08/2026, decisão do usuário): a 1ª sequência do
    // roteiro (menor G2_OPERAC) está sempre liberada; as seguintes só liberam
    // depois que a sequência anterior já teve algum apontamento real registrado
    // NESSA OP (ver historico/mapApontamento — H6_OPERAC de cada apontamento).
    // Ex.: sequência 020 só aparece pra escolher depois que 010 já foi apontada.
    const operacoesOrdenadas = useMemo(
        () => [...operacoesRoteiro].sort((a, b) => Number(a.G2_OPERAC) - Number(b.G2_OPERAC)),
        [operacoesRoteiro]
    );
    const operacoesComApontamento = useMemo(
        () => new Set([
            ...historico.map((h) => h.operacao).filter(Boolean),
            // O total por operação combina ZH6 e SH6 no backend. Portanto,
            // produção positiva também comprova que a etapa foi apontada,
            // mesmo quando a linha correspondente não veio no histórico do
            // preview. Isso mantém a liberação igual à regra do backend.
            ...Object.entries(produzidoPorOperacaoPreview)
                .filter(([, produzido]) => Number(produzido) > 0)
                .map(([operacao]) => operacao),
        ]),
        [historico, produzidoPorOperacaoPreview]
    );
    const sequenciasLiberadas = useMemo(() => {
        const liberadas = new Set();
        operacoesOrdenadas.forEach((seq, i) => {
            if (i === 0 || operacoesComApontamento.has(operacoesOrdenadas[i - 1].G2_OPERAC)) {
                liberadas.add(seq.G2_OPERAC);
            }
        });
        return liberadas;
    }, [operacoesOrdenadas, operacoesComApontamento]);

    // recursoEfetivo (25/08/2026) — reatribuição ativa, se existir pra essa
    // operação, senão a G2_RECURSO crua do roteiro. Mesma regra do backend
    // (operacoesPendentesNaMaquina, producao.js), só que aqui em cima do
    // roteiro inteiro (não uma operação só), pra decidir quais sequências
    // oferecer nesta máquina.
    const candidatosMaquina = useMemo(
        () => operacoesRoteiro.filter((o) => {
            const operac = (o.G2_OPERAC || '').trim();
            const recursoEfetivo = reatribuicoesPorOperacaoPreview[operac] || (o.G2_RECURSO || '').trim();
            return recursoEfetivo === recursoSessao;
        }),
        [operacoesRoteiro, recursoSessao, reatribuicoesPorOperacaoPreview]
    );
    // "Liberada" só olha se a etapa ANTERIOR já foi apontada — não diz nada
    // sobre a própria etapa já ter terminado. Separado em 2 passos (liberadas
    // vs. ainda-não-concluídas) pra dar mensagens diferentes: "aguardando a
    // etapa anterior" continua correto quando NADA foi liberado ainda; um
    // caso novo (candidatosLiberados > 0 mas candidatosSequencia = 0) precisa
    // de outra mensagem — "já foi tudo produzido nesta máquina", não
    // "aguardando" (achado real 13/08/2026: operação apontada fora do Persona,
    // já com produzido >= quantidade, mas nunca totalizada no Protheus,
    // continuava aparecendo como opção de sequência pra escolher).
    const candidatosLiberados = useMemo(
        () => candidatosMaquina.filter((o) => sequenciasLiberadas.has(o.G2_OPERAC)),
        [candidatosMaquina, sequenciasLiberadas]
    );
    const candidatosSequencia = useMemo(
        () => candidatosLiberados.filter((o) => {
            const produzidoNaOperacao = produzidoPorOperacaoPreview[o.G2_OPERAC] || 0;
            // Meta ajustada (14/08/2026): a 1ª operação do roteiro pode ter
            // meta maior que a quantidade flat (refugo em operações
            // posteriores) — sem isso, ela nunca reaparece aqui como opção
            // depois de "reaberta", mesmo já reaparecendo na lista de OPs.
            const meta = metaPorOperacaoPreview[o.G2_OPERAC] ?? (opPreview?.quantidadeAProduzir || 0);
            return !(meta > 0 && produzidoNaOperacao >= meta);
        }),
        [candidatosLiberados, produzidoPorOperacaoPreview, metaPorOperacaoPreview, opPreview]
    );
    // Sempre pergunta (painel do lado direito, dentro do próprio modal de OP) —
    // mesmo quando só existe 1 sequência liberada, o operador confirma explicitamente,
    // não pula sozinho (pedido explícito do usuário).
    const operacaoSessao = sequenciaEscolhida?.G2_OPERAC || '';
    const primeiraOperacaoRoteiro = String(operacoesOrdenadas[0]?.G2_OPERAC || '').trim();
    const podeFazerNovaRequisicao = operacaoSessao === primeiraOperacaoRoteiro
        && materiaisOP.some((item) => Number(item.quantidade_pendente) > 0);
    useEffect(() => {
        if (!op?.id || !operacaoSessao || operacaoSessao !== primeiraOperacaoRoteiro) {
            setMateriaisOP([]);
            return;
        }
        // A abertura inicial do modal já consulta os materiais. Esta leitura
        // silenciosa existe apenas para retomada/reload da tela de apontamentos.
        if (modal === 'materiais' || materiaisOP.length > 0) return;
        let ativo = true;
        api.get(`/api/producao/${op.id}/materiais`)
            .then((resposta) => {
                if (ativo) setMateriaisOP(resposta.materiais || []);
            })
            .catch(() => { });
        return () => { ativo = false; };
    }, [op?.id, operacaoSessao, primeiraOperacaoRoteiro, modal, materiaisOP.length]);
    const ultimaOperacaoRoteiro = operacoesOrdenadas.length
        ? String(operacoesOrdenadas[operacoesOrdenadas.length - 1].G2_OPERAC || '').trim()
        : '';
    const naUltimaOperacao = !!operacaoSessao && operacaoSessao === ultimaOperacaoRoteiro;
    const semOperacaoNoRoteiro = !carregandoOperacoes && operacoesRoteiro.length > 0 && candidatosMaquina.length === 0;
    const aguardandoEtapaAnterior = !carregandoOperacoes && candidatosMaquina.length > 0 && candidatosLiberados.length === 0;
    const todasOperacoesJaConcluidas = !carregandoOperacoes && candidatosLiberados.length > 0 && candidatosSequencia.length === 0;

    // Operação imediatamente ANTERIOR à escolhida no roteiro (13/08/2026,
    // pedido do usuário) — null na 1ª operação (não tem "anterior", nada pra
    // limitar). Usado só pra exibir/limitar; o teto de verdade é validado no
    // servidor (criarApontamento), que recalcula isso por conta própria.
    const indiceSequenciaAtual = operacoesOrdenadas.findIndex((o) => o.G2_OPERAC === operacaoSessao);
    const operacaoAnterior = indiceSequenciaAtual > 0 ? operacoesOrdenadas[indiceSequenciaAtual - 1] : null;
    const produzidoOperacaoAnterior = operacaoAnterior
        ? (produzidoPorOperacao[operacaoAnterior.G2_OPERAC] || 0)
        : null;
    // Quanto ainda dá pra apontar nesta operação sem passar do que a anterior
    // entregou — null quando não há operação anterior (1ª do roteiro, sem teto).
    // Bug real 14/08/2026 (achado do usuário): só descontava produzidoAcumulado,
    // esquecendo refugadoAcumulado — 35 produzidas + 1 refugada = 36 (o total
    // recebido) mostrava saldo 1 em vez de 0, e por causa disso o "Iniciar
    // Produção" (desabilitado só quando saldo===0, ver mais abaixo) continuava
    // liberado. Refugo TAMBÉM sai do mesmo lote recebido, mesma regra já
    // aplicada na validação de teto (ModalPausa/ModalQuantidades/criarApontamento).
    const saldoOperacaoAnterior = produzidoOperacaoAnterior != null
        ? Math.max(produzidoOperacaoAnterior - produzidoAcumulado - refugadoAcumulado, 0)
        : null;
    // Aviso contextual único (18/08/2026, pedido do usuário) — antes eram até
    // 3 blocos coloridos empilhados de largura total no topo da tela
    // (concorrendo entre si e com o próprio banner duplicado que piscava, ver
    // remoção acima). Agora só o mais relevante aparece, anexado ao próprio
    // cabeçalho de status/OP (não mais um bloco à parte). Prioridade: sem
    // saldo da operação anterior (trava o apontamento, o mais urgente) >
    // sequência reaberta por refugo posterior (informativo, explica a meta
    // maior) > saldo normal da operação anterior (informativo neutro).
    // carregandoSessao (25/08/2026): nunca calcula o aviso em cima de
    // produzidoAcumulado/refugadoAcumulado/produzidoPorOperacao ainda zerados
    // pelo reset síncrono de handleSelecionarOP — ver comentário na
    // declaração do state. Sem isso, mostrava um aviso vermelho errado
    // ("já apontado o total, não é possível apontar mais") por um instante,
    // toda vez que uma OP nova era selecionada.
    const avisoAtivo = carregandoSessao ? null : (operacaoAnterior && saldoOperacaoAnterior === 0)
        ? {
            tipo: 'red', Icone: AlertTriangle, texto: (
                <>Recebido da operação {operacaoAnterior.G2_OPERAC}: <strong>{produzidoOperacaoAnterior}</strong> peça(s); já apontado o total recebido, não é possível apontar mais nesta operação.</>
            ),
        }
        : status === 'REABERTA'
        ? {
            tipo: 'violet', Icone: RefreshCw, texto: (
                <>Meta desta sequência foi ajustada para <strong>{op?.quantidadeAProduzir}</strong> peça(s), pois refugo registrado em etapas seguintes do roteiro reduziu o total de peças boas esperado no final; por ser a 1ª sequência do roteiro, ela é quem compensa essa perda.</>
            ),
        }
        : operacaoAnterior
        ? {
            tipo: 'petrol', Icone: CheckCircle, texto: (
                <>Recebido da operação {operacaoAnterior.G2_OPERAC}: <strong>{produzidoOperacaoAnterior}</strong> peça(s); ainda dá pra apontar até {saldoOperacaoAnterior} peça(s) nesta operação.</>
            ),
        }
        : null;
    // Saldo da PRÓPRIA meta desta operação (14/08/2026, estendido 18/08/2026).
    // Quando existe operação anterior, o refugado sai do mesmo lote físico
    // recebido dela (mesma regra de saldoOperacaoAnterior acima) — continua
    // descontado aqui também, como checagem redundante de segurança.
    // Bug real 18/08/2026 (achado do usuário, OP 12935301001): na 1ª operação
    // (sem operação anterior, sem lote físico limitando), a meta pode estar
    // AJUSTADA pra compensar refugo de operações posteriores (ver
    // calcularMetaAjustadaPrimeiraOperacao) — é um alvo de PEÇA BOA a entregar,
    // não um orçamento de tentativas. Descontar o refugo da própria 1ª
    // operação desse alvo fazia o operador nunca conseguir apontar as peças
    // boas extras que a meta ajustada existe justamente pra permitir (ex.:
    // meta 650 = 600 planejado + 50 refugados numa operação seguinte, já
    // produzido 600 + 20 refugado na própria 1ª operação — "saldo" mostrava só
    // 30 em vez dos 50 que realmente faltavam produzir de bom).
    const saldo = op
        ? Math.max(op.quantidadeAProduzir - produzidoAcumulado - (operacaoAnterior ? refugadoAcumulado : 0), 0)
        : 0;
    // "Iniciar Produção" trava por 2 motivos independentes (14/08/2026): (1)
    // sem saldo do que a operação ANTERIOR entregou (não existe na 1ª
    // operação, sem teto contra a anterior); (2) sem saldo da PRÓPRIA meta
    // desta operação (`saldo`, já usa a meta ajustada quando é a 1ª — ver
    // op.quantidadeAProduzir/handleSelecionarOP) — esse segundo motivo agora
    // vale pra qualquer operação, inclusive a 1ª, que antes não tinha teto
    // nenhum (achado real: operador apontando bem além da meta, sem bloqueio).
    const semSaldoOperacaoAnterior = operacaoAnterior != null && saldoOperacaoAnterior === 0;
    const semSaldoPropriaMeta = saldo === 0;
    const iniciarDesabilitado = semSaldoOperacaoAnterior || semSaldoPropriaMeta;
    const iniciarTooltip = semSaldoOperacaoAnterior
        ? 'Já foi apontado tudo que a operação anterior entregou, nada mais pra produzir aqui.'
        : semSaldoPropriaMeta
            ? 'Já foi apontado o total esperado desta operação, nada mais pra produzir aqui.'
            : undefined;
    // Rótulos "Setup" (21/08/2026, pedido do usuário): quando o tipo de hora
    // é Preparação (código 3), os 3 botões de ação trocam de nome — "Iniciar
    // Setup"/"Pausar Setup"/"Liberar Máquina" em vez de "Iniciar/Pausar/Apontar
    // Produção". `tipoHoraRelevante` junta as 3 fontes possíveis, da mais
    // concreta pra mais cedo: tipoHoraAtiva (ciclo JÁ rodando) > tipoHoraSessao
    // (escolha já confirmada no modal, pendente do próximo clique) >
    // `exigeSetup` (nem abriu o modal ainda, mas já dá pra saber que só resta
    // Preparação como opção — ver ModalTipoHora). Corrige o bug real
    // (24/08/2026, usuário reportou com print): sem isso, "Apontar Produção"
    // continuava com o nome velho enquanto o modal ainda estava aberto,
    // mesmo já travado em Preparação, só atualizando depois do 2º clique.
    const tipoHoraRelevante = tipoHoraAtiva || tipoHoraSessao || (!setupFeito ? '3' : '');
    // Repreparação (6) conta como setup também (25/08/2026, pedido do
    // usuário: "é basicamente um setup, caso aconteça algum problema") —
    // mesmos botões, mesma gravação na ZS1 (nunca ZH6), mesmo tratamento de
    // Preparação em todo o resto deste arquivo e do backend (producao.js).
    const emSetup = tipoHoraRelevante === '3' || tipoHoraRelevante === '6';
    // Rótulos por tipo de hora (27/08/2026, pedido do usuário) — antes eram só
    // 2 pares (Produção cobria 1+2, Setup cobria 3+6, ver `emSetup` acima).
    // Agora existem 4 pares, um por tipo de hora selecionável (TIPOS_HORA) —
    // Produção/Retrabalho seguem a MESMA gravação (ZH6) e Setup/Repreparação
    // idem (ZS1), só o texto exibido muda. `emSetup` continua existindo tal
    // qual pra decidir o FLUXO (ZS1 vs ZH6, modal de liberar máquina etc.) —
    // só os rótulos passam a olhar o tipo exato.
    const ROTULOS_POR_TIPO_HORA = {
        '1': { iniciar: 'Iniciar Produção', pausar: 'Pausar Produção', apontar: 'Apontar Produção' },
        '2': { iniciar: 'Iniciar Retrabalho', pausar: 'Pausar Retrabalho', apontar: 'Apontar Retrabalho' },
        '3': { iniciar: 'Iniciar Setup', pausar: 'Pausar Setup', apontar: 'Liberar Máquina' },
        '6': { iniciar: 'Iniciar Repreparação', pausar: 'Pausar Repreparação', apontar: 'Liberar Máquina' },
        // Produção Especial (29/08/2026) — nunca escolhido no modal (ver
        // TIPOS_HORA.filter acima); só chega aqui via a OP marcada como
        // Projeto Especial (auto, ver handleIniciarClick).
        '4': { iniciar: 'Iniciar Produção Especial', pausar: 'Pausar Produção Especial', apontar: 'Apontar Produção Especial' },
    };
    const rotulosTipoHora = ROTULOS_POR_TIPO_HORA[tipoHoraRelevante] || ROTULOS_POR_TIPO_HORA['1'];
    const rotuloIniciar = rotulosTipoHora.iniciar;
    const rotuloPausar = rotulosTipoHora.pausar;
    const rotuloApontar = rotulosTipoHora.apontar;
    // Restante que dá pra apontar num único lançamento (ModalPausa/ModalQuantidades)
    // — 2 tetos independentes, passados separados pro modal desde 21/08/2026
    // (antes eram combinados aqui num só `saldoMaximoPermitido = min(...)`,
    // ambos bloqueio duro). Agora só `saldoOperacaoAnterior` (o que a
    // operação anterior do roteiro já entregou — mesmo lote físico) continua
    // bloqueio duro sem exceção; `saldo` (a própria meta desta operação,
    // baseada na quantidade da OP) passou a ser ultrapassável com
    // justificativa (pedido do usuário) — ver limiteFisico/limiteMeta nos
    // modais. saldoOperacaoAnterior null = sem teto físico (1ª operação do
    // roteiro).

    // Só busca as OPs depois que a máquina foi resolvida (login ou
    // ModalSelecionarMaquinaLogin) — filtra no backend pra só trazer OPs cujo
    // roteiro passa por essa máquina (04/08/2026, decisão do usuário).
    useEffect(() => {
        if (!recursoSessao) return;
        setCarregandoOrdens(true);
        api.post(`/api/producao/sincronizar-protheus?maquina=${encodeURIComponent(recursoSessao)}`)
            .then(rows => setOrdensDisponiveis(rows.map(normalizarOrdem)))
            .catch(err => setErroApi(err.message || 'Erro ao carregar ordens de produção.'))
            .finally(() => setCarregandoOrdens(false));
    }, [recursoSessao]);

    // Clicar numa OP na lista (sem confirmar ainda) já dispara a busca do
    // roteiro + histórico de apontamentos — é o que faz o painel de sequência
    // aparecer do lado direito na hora, na mesma tela (pedido do usuário
    // 04/08/2026), em vez de só depois de confirmar a seleção.
    const handlePreVisualizarOP = (o) => {
        setOpPreview(o);
        setSequenciaEscolhida(null);
        setOperacoesRoteiro([]);
        setHistorico([]);
        setProduzidoPorOperacaoPreview({});
        setMetaPorOperacaoPreview({});
        setReatribuicoesPorOperacaoPreview({});
        if (!o.produto || !o.roteiro) return; // sem roteiro sincronizado, sem sequência pra escolher — mesma resiliência de sempre
        setCarregandoOperacoes(true);
        Promise.all([
            api.get(`/api/protheus/roteiro?produto=${encodeURIComponent(o.produto)}&roteiro=${encodeURIComponent(o.roteiro)}`),
            api.get(`/api/producao/${o.id}/sessao`).catch(() => ({ ordem: {}, apontamentos: [] })),
        ])
            .then(([operacoes, sessao]) => {
                setOperacoesRoteiro(operacoes);
                setHistorico((sessao.apontamentos || []).map(mapApontamento));
                // obterSessao devolve { ordem: {...}, apontamentos: [...] } — produzido_por_operacao
                // mora DENTRO de ordem, não no nível raiz (bug meu: primeira versão lia
                // sessao.produzido_por_operacao direto, sempre undefined -> caía no || {}).
                setProduzidoPorOperacaoPreview(sessao.ordem?.produzido_por_operacao || {});
                setMetaPorOperacaoPreview(sessao.ordem?.meta_por_operacao || {});
                setReatribuicoesPorOperacaoPreview(sessao.ordem?.reatribuicoes_por_operacao || {});
            })
            .catch(() => setOperacoesRoteiro([]))
            .finally(() => setCarregandoOperacoes(false));
    };

    // "Trocar de máquina" (limparMaquina) só limpava a máquina no auth store —
    // o preview de OP/sequência (opPreview/operacoesRoteiro/historico) é
    // estado local desta tela, sobrevive à troca (mesmo componente, sem
    // remount) e ficava "grudado" mostrando a sequência de uma OP que só
    // fazia sentido pra máquina anterior (bug real, 06/08/2026: confirmado
    // com dado real que uma OP pode nem existir no roteiro da nova máquina).
    const handleTrocarMaquina = () => {
        setOpPreview(null);
        setSequenciaEscolhida(null);
        setOperacoesRoteiro([]);
        setHistorico([]);
        setProduzidoPorOperacaoPreview({});
        setMetaPorOperacaoPreview({});
        setReatribuicoesPorOperacaoPreview({});
        limparMaquina();
        buscarMaquinasUsuario();
    };

    // Busca a lista de máquinas do usuário AO VIVO (13/08/2026) — antes abria o
    // mesmo retrato tirado no login (até 8h desatualizado). Resolvida no servidor
    // a partir do usuário autenticado (ver routes/auth.js). Também alimenta a
    // telinha de escolha de máquina no novo fluxo (17/09/2026, pedido do usuário):
    // quem não vem com máquina resolvida no login nunca mais fica preso no aviso
    // "Seu usuário não tem nenhuma máquina liberada." — busca aqui e mostra as
    // candidatas pra escolher (máquina → OP → sequência → Confirmar → apontamento).
    const buscarMaquinasUsuario = () => {
        setCarregandoMaquinas(true);
        api.get('/api/producao/maquinas-disponiveis')
            .then(({ maquinasDisponiveis, totalMaquinasCentroTrabalho }) => atualizarMaquinasDisponiveis(maquinasDisponiveis, totalMaquinasCentroTrabalho))
            .catch(() => { /* mantém a lista antiga em caso de falha — resiliência de sempre */ })
            .finally(() => setCarregandoMaquinas(false));
    };

    // Fallback do novo fluxo (17/09/2026): se o login não resolveu a máquina
    // sozinho e a lista de candidatas ainda não veio (persistência antiga ou
    // login sem esse dado), busca na hora pra telinha de escolha de máquina
    // nunca abrir vazia por falta de dado, e sim só quando realmente não há
    // máquina alguma.
    useEffect(() => {
        if (!operadorLogado?.maquina && !(operadorLogado?.maquinasDisponiveis || []).length && !carregandoMaquinas) {
            buscarMaquinasUsuario();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Confirma a OP já com a sequência resolvida no preview (ver
    // handlePreVisualizarOP) — não mexe mais em operacoesRoteiro/sequenciaEscolhida,
    // só busca de novo a sessão completa (produzido/status/tempo/etc.) pra ter
    // certeza que está com o dado mais fresco no momento de assumir a OP.
    const handleSelecionarOP = async (opSelecionada) => {
        setOp(opSelecionada);
        setOpPreview(null);
        // Tipo de Hora (21/08/2026, pedido do usuário) — a tela de
        // apontamentos (com o desenho, os botões etc.) já monta por baixo
        // enquanto a sessão carrega, mas o ModalTipoHora SÓ abre depois que a
        // busca da sessão volta (ver setModal('tipoHora') no fim do try
        // abaixo, junto com setSetupFeito). CORRIGIDO em 25/08/2026 — antes
        // abria aqui, na hora, com setSetupFeito(false) logo depois forçando
        // exigeSetup=true de cara; se a OP na verdade já tinha setup feito,
        // a resposta da API virava setSetupFeito(true) um instante depois e
        // o modal "piscava", trocando de conteúdo (Setup necessário -> grid
        // completo) bem na frente do operador.
        //
        // setModal(null) aqui (25/08/2026, 2º achado do mesmo problema) —
        // `modal` começa em 'selecao' (valor inicial do useState, ver linha
        // ~1026) e antes só saía desse valor porque este mesmo lugar já
        // fazia setModal('tipoHora') na hora. Sem isso, ficava 'selecao'
        // durante todo o intervalo do fetch — e como `op` já tinha virado
        // verdadeiro (setOp acima), o overlay de ModalSelecionarOP (o que
        // aparece quando `modal === 'selecao'` com uma OP já ativa, ver
        // botão "Trocar OP") piscava por cima da tela de apontamentos antes
        // do ModalTipoHora abrir.
        setModal(null);
        setCarregandoSessao(true);
        setProduzidoAcumulado(0);
        setRefugadoAcumulado(0);
        setProduzidoPorOperacao({});
        setHistorico([]);
        setStatus('AGUARDANDO');
        resetElapsed(0);
        setRunning(false);
        setHoraInicio('--:--');
        setParadasCount(0);
        setImagemPeca(null);
        setDesenhoPeca(null);
        setPfiPeca(null);
        setSetupFeito(false);
        setTipoHoraAtiva('');
        setEspecial(false);

        try {
            // ?operacao= (13/08/2026): a sequência já foi escolhida no preview
            // (ver handlePreVisualizarOP/ModalSelecionarOP) — sem isso,
            // produzido_total vinha somado de TODAS as operações do roteiro
            // (cada uma processa o mesmo lote físico em sequência, não é
            // aditivo entre elas — ver obterSessao no backend).
            const operacaoParaBuscar = sequenciaEscolhida?.G2_OPERAC || '';
            const { ordem, apontamentos } = await api.get(
                `/api/producao/${opSelecionada.id}/sessao${operacaoParaBuscar ? `?operacao=${encodeURIComponent(operacaoParaBuscar)}` : ''}`
            );
            // Meta ajustada (14/08/2026): só a 1ª operação do roteiro pode vir
            // maior que a quantidade flat (refugo em operações posteriores —
            // ver meta_por_operacao/obterSessao no backend).
            setOp((o) => ({ ...o, quantidadeAProduzir: Number(ordem.meta_por_operacao?.[operacaoParaBuscar] ?? ordem.quantidade) }));
            setProduzidoAcumulado(Number(ordem.produzido_total) || 0);
            setRefugadoAcumulado(Number(ordem.refugado_total) || 0);
            setProduzidoPorOperacao(ordem.produzido_por_operacao || {});
            setHistorico(apontamentos.map(mapApontamento));
            setSetupFeito(!!ordem.setup_feito);
            setEspecial(!!ordem.especial);
            // Só agora, com o setup_feito real em mãos, o modal abre já com o
            // conteúdo certo (ver comentário no início da função). Projeto
            // Especial com setup já feito (29/08/2026) não precisa perguntar
            // nada — pula o modal, handleIniciarClick vai direto pro código 4
            // quando o operador clicar em Iniciar Produção.
            const primeiraOperacao = String(operacoesOrdenadas[0]?.G2_OPERAC || '').trim();
            if (EXIBIR_MATERIAIS_OP && operacaoParaBuscar && operacaoParaBuscar === primeiraOperacao) {
                // Quando ainda falta setup, consulta os materiais em segundo
                // plano. Abrir o modal aqui fazia "Materiais da OP" piscar por
                // alguns milissegundos antes do aviso "Setup necessário".
                const consultaMateriais = await carregarMateriaisOP(opSelecionada.id, {
                    abrirModal: !!ordem.setup_feito,
                });
                const materiais = consultaMateriais.materiais || [];
                if (!ordem.setup_feito || consultaMateriais.ja_requisitado || !materiais.some((item) => Number(item.quantidade_pendente) > 0)) {
                    setModal(ordem.especial && ordem.setup_feito ? null : 'tipoHora');
                }
            } else if (!(ordem.especial && ordem.setup_feito)) {
                setModal('tipoHora');
            }
            // Nunca retoma o cronômetro automaticamente: se estava em produção, volta como pausada.
            setStatus(ordem.status === 'EM PRODUCAO' ? 'PARADO' : (ordem.status || 'AGUARDANDO'));
            resetElapsed(ordem.tempo_acumulado_segundos || 0);
            setRunning(false);
            setHoraInicio(ordem.hora_inicio_turno || '--:--');
            setParadasCount(ordem.paradas_count || 0);
            setImagemPeca(apiUrl(ordem.imagem_url));
            setDesenhoPeca(apiUrl(ordem.desenho_url));
            setPfiPeca(apiUrl(ordem.pfi_url));
        } catch (err) {
            if (tratarOpEncerrada(err)) return;
            setErroApi(err.message || 'Erro ao carregar a sessão desta OP.');
        } finally {
            setCarregandoSessao(false);
        }
    };

    // Assume a OP em andamento como a atual da tela (monta todo o estado que
    // handleSelecionarOP montaria) — só chamado depois que o operador confirma
    // o aviso (ModalOpEmAndamento), nunca sozinho (pedido do usuário
    // 06/08/2026: "reconhecer e avisar", não pular direto sem avisar).
    const aplicarSessaoComoOpAtual = async (codigo, { ordem, apontamentos, _setup, _recurso, _recursoNome }) => {
        // `modal` começa em 'selecao' (valor inicial do useState) — sem isso,
        // ao confirmar o aviso a tela troca de OP mas o modal de escolher OP
        // continua aberto por cima (nunca tinha sido fechado nesse fluxo).
        setModal(null);
        setOp({
            id: codigo,
            codigo: ordem.codigo,
            produto: ordem.produto,
            produtoNome: ordem.produto_nome || ordem.produto,
            roteiro: ordem.roteiro,
            // Prefere _recurso/_recursoNome (25/08/2026) quando vieram — a
            // máquina achada pode ser DIFERENTE da atual (recursoSessao); e
            // como definirMaquina() só reflete no próximo render (o state do
            // Zustand não muda na hora dentro desta mesma função), usar
            // recursoCodigoNomeSessao aqui pegaria o valor ANTIGO, ainda não
            // atualizado. _recursoNome já vem certo desde a checagem do
            // useEffect, então usa ele quando disponível.
            maquina: (_recurso && _recursoNome) ? `${_recurso} - ${_recursoNome}` : recursoCodigoNomeSessao,
            quantidadeAProduzir: Number(ordem.quantidade),
            produzido: Number(ordem.produzido_total) || 0,
            refugado: Number(ordem.refugado_total) || 0,
        });
        setHistorico(apontamentos.map(mapApontamento));

        // A busca que trouxe `ordem` (useEffect de "OP em andamento" acima)
        // ainda não sabia qual operação estava ativa — veio sem `?operacao=`,
        // então produzido/tempo/status vieram da OP inteira misturada (mesmo
        // comportamento antigo, só mantido quando `operacao` não é informado
        // — ver obterSessao no backend). Agora que sabemos (operacao_ativa),
        // busca de novo já escopado. `apontamentos` (histórico completo da OP,
        // usado pra sequenciasLiberadas) não muda entre as duas chamadas, não
        // precisa buscar de novo.
        //
        // Setup (21/08/2026) — quando `_setup` vem preenchido, a operação
        // ativa é a do setup (ZS1), não `ordem.operacao_ativa` (campo derivado
        // da ZH6, sempre vazio aqui — setup não grava lá). A `ordem` já veio
        // escopada certa desde o useEffect (fetch já usou `?operacao=`), então
        // pula o refetch abaixo.
        const operacaoAtiva = _setup ? _setup.operacao : (ordem.operacao_ativa || '');
        let ordemEscopada = ordem;
        if (!_setup && operacaoAtiva) {
            try {
                const resEscopado = await api.get(`/api/producao/${codigo}/sessao?operacao=${encodeURIComponent(operacaoAtiva)}`);
                ordemEscopada = resEscopado.ordem;
            } catch { /* mantém o não-escopado como fallback — resiliência de sempre */ }
        }

        // Meta ajustada (14/08/2026) — mesmo ajuste de handleSelecionarOP.
        setOp((o) => ({ ...o, quantidadeAProduzir: Number(ordemEscopada.meta_por_operacao?.[operacaoAtiva] ?? ordemEscopada.quantidade) }));
        setProduzidoAcumulado(Number(ordemEscopada.produzido_total) || 0);
        setRefugadoAcumulado(Number(ordemEscopada.refugado_total) || 0);
        setProduzidoPorOperacao(ordemEscopada.produzido_por_operacao || {});
        setSetupFeito(!!ordemEscopada.setup_feito);
        setEspecial(!!ordemEscopada.especial);

        // Status/tempo/cronômetro (21/08/2026): setup usa `_setup` como fonte
        // (ZS1, via ZH6_STATUS/ZH6_GRAVAD equivalentes) em vez de
        // `ordemEscopada` (ZH6, sempre vazio pra um ciclo de setup). Mesma
        // conta de "tempo acumulado + decorrido desde o início" nos dois
        // casos, só a origem dos dados muda.
        const statusFonte = _setup ? (_setup.status === 'PAUSADO' ? 'PARADO' : 'EM PRODUCAO') : (ordemEscopada.status || 'AGUARDANDO');
        const tempoBaseFonte = _setup ? (_setup.tempoAcumuladoSegundos || 0) : (ordemEscopada.tempo_acumulado_segundos || 0);
        const sessaoIniciadaEmFonte = _setup ? _setup.sessaoIniciadaEm : ordemEscopada.sessao_iniciada_em;
        // '3' de fallback (27/08/2026) — cobre setup retomado de antes do
        // campo ZS1_TIPO existir; com o campo presente, respeita se era
        // Preparação('3') ou Repreparação('6') de verdade (bug real: antes
        // sempre voltava mostrando "Setup", nunca "Repreparação", ao retomar).
        setTipoHoraAtiva(_setup ? (_setup.tipoHora || '3') : '');
        // Diferente de handleSelecionarOP (reseleção manual, que sempre volta
        // pausada): aqui é o próprio operador confirmando que já estava com essa
        // OP (ou setup) rodando — volta no status real (roda o cronômetro se
        // tava Em Produção), sem precisar clicar em Iniciar de novo.
        setStatus(statusFonte);
        // tempo_acumulado_segundos fica congelado no valor de quando o "Iniciar"
        // foi clicado (só atualiza de novo em Pausar/Apontar) — se ainda tava Em
        // Produção, soma o tempo real que passou desde então (sessao_iniciada_em),
        // senão o cronômetro "zerava" (voltava pro valor congelado) toda vez que
        // o operador saía e voltava pro app (achado real 06/08/2026).
        const decorridoDesdeInicio = statusFonte === 'EM PRODUCAO' && sessaoIniciadaEmFonte
            ? Math.max(0, Math.floor((Date.now() - new Date(sessaoIniciadaEmFonte).getTime()) / 1000))
            : 0;
        resetElapsed(tempoBaseFonte + decorridoDesdeInicio);
        setRunning(statusFonte === 'EM PRODUCAO');
        setHoraInicio(_setup?.horaInicioReal || ordemEscopada.hora_inicio_turno || '--:--');
        setParadasCount(ordemEscopada.paradas_count || 0);
        setImagemPeca(apiUrl(ordemEscopada.imagem_url));
        setDesenhoPeca(apiUrl(ordemEscopada.desenho_url));
        setPfiPeca(apiUrl(ordemEscopada.pfi_url));

        if (ordem.produto && ordem.roteiro) {
            try {
                const operacoes = await api.get(`/api/protheus/roteiro?produto=${encodeURIComponent(ordem.produto)}&roteiro=${encodeURIComponent(ordem.roteiro)}`);
                setOperacoesRoteiro(operacoes);
                setSequenciaEscolhida(operacoes.find((o) => o.G2_OPERAC === operacaoAtiva) || null);
            } catch { /* sem roteiro não impede continuar — mesma resiliência de sempre */ }
        }
    };

    // Clique em "OK" no aviso de OP em andamento (ModalOpEmAndamento).
    // Troca a máquina ativa ANTES de reconstruir a sessão (25/08/2026) — o
    // em-andamento agora é buscado por operador, então pode ter sido achado
    // numa máquina DIFERENTE da atual (recursoSessao); sem isso, os próximos
    // Pausar/Apontar/Iniciar Setup mandariam `recurso: recursoSessao`
    // (errado, a máquina antiga) em vez da máquina onde a OP/setup realmente
    // está aberta.
    const handleConfirmarOpEmAndamento = () => {
        if (!opEmAndamento) return;
        if (opEmAndamento._recurso && opEmAndamento._recurso !== recursoSessao) {
            definirMaquina(opEmAndamento._recurso, opEmAndamento._recursoNome);
        }
        aplicarSessaoComoOpAtual(opEmAndamento.codigo, opEmAndamento);
        setOpEmAndamento(null);
    };

    // Escopado pelo OPERADOR, não pela máquina atual (25/08/2026, pedido do
    // usuário) — achado real: antes só checava a máquina resolvida agora
    // (recursoSessao); se o operador tinha uma OP/setup "Em Produção" numa
    // máquina e acessava OUTRA (mais de um centro de trabalho liberado),
    // nada avisava — dava pra iniciar uma 2ª OP ali com a 1ª ainda aberta em
    // outro lugar. Roda assim que operadorLogado existe (não depende mais só
    // de recursoSessao) — busca os dados e guarda em opEmAndamento pra
    // mostrar o aviso (não pula direto — pedido do usuário 06/08/2026:
    // avisar e deixar confirmar).
    useEffect(() => {
        const operador = operadorLogado?.nome;
        if (!operador) return;
        setVerificandoEmAndamento(true);
        api.get(`/api/producao/em-andamento?operador=${encodeURIComponent(operador)}`)
            .then(async ({ op: codigo, tipo, recurso, recurso_nome, operacao, status_setup, sessao_iniciada_em, hora_inicio_real, tempo_acumulado_segundos, tipo_hora }) => {
                if (!codigo) return;
                // Setup (21/08/2026) — não aparece na ZH6 (obterSessao mostraria
                // "Aguardando" errado), então busca já escopado pela operação e
                // guarda os dados do setup à parte (`_setup`) pra
                // aplicarSessaoComoOpAtual reconstruir o estado certo. Mesmo
                // formato de sessao_iniciada_em/tempo_acumulado_segundos que a
                // ZH6 já usa — reaproveita a mesma conta de "quanto tempo
                // passou desde então" (ver aplicarSessaoComoOpAtual).
                const query = tipo === 'setup' && operacao ? `?operacao=${encodeURIComponent(operacao)}` : '';
                const sessao = await api.get(`/api/producao/${codigo}/sessao${query}`);
                setOpEmAndamento({
                    codigo, ...sessao,
                    // Máquina onde o em-andamento foi achado (25/08/2026) —
                    // pode ser DIFERENTE da máquina atual (recursoSessao); ao
                    // confirmar, handleConfirmarOpEmAndamento troca a máquina
                    // ativa pra essa antes de retomar (ver definirMaquina).
                    _recurso: recurso || null,
                    _recursoNome: recurso_nome || null,
                    _setup: tipo === 'setup'
                        ? { operacao, status: status_setup, sessaoIniciadaEm: sessao_iniciada_em, horaInicioReal: hora_inicio_real, tempoAcumuladoSegundos: tempo_acumulado_segundos, tipoHora: tipo_hora }
                        : null,
                });
            })
            .catch(() => { }) // resiliente: se falhar, só não avisa — cai no fluxo normal de escolher OP
            .finally(() => setVerificandoEmAndamento(false));
    }, [operadorLogado?.nome]);

    // Auto-atualização da sessão (21/08/2026, achado real do usuário) — sem
    // isso, Produzido/Saldo/Meta/Início/Paradas só refletiam o servidor no
    // instante em que a tela abriu (aplicarSessaoComoOpAtual), e nunca mais —
    // se outro operador apontasse na MESMA OP/operação nesse meio-tempo (troca
    // de turno, outro terminal), esta tela continuava mostrando a fotografia
    // antiga indefinidamente (só corrigia com F5 manual). O cronômetro
    // "parecia" certo porque é 100% local (Timer/elapsedRef), sem nenhuma
    // relação com esses outros campos — daí o "Início" e o "Produzido"
    // ficarem visivelmente desatualizados enquanto o tempo seguia contando
    // normal. Mesmo intervalo (15s) já usado em Ordens de Produção.
    //
    // Escopo deliberado: só reconcilia os campos de DADO (produzido, saldo,
    // meta, início, paradas, imagem/desenho) — não mexe em status/running.
    // Mudar status sozinho (ex.: parar o cronômetro porque outra sessão
    // pausou) é uma decisão mais delicada, fora do que foi pedido aqui; as
    // transições de status continuam só pelas ações explícitas do operador
    // (Iniciar/Pausar/Apontar). Também não roda com um modal aberto — evita
    // atualizar valores embaixo de uma tela de confirmação (ex.: Resumo antes
    // de Apontar) enquanto o operador está decidindo algo em cima deles.
    useEffect(() => {
        if (!op?.id || modal !== null) return;
        const intervalId = setInterval(() => {
            const query = operacaoSessao ? `?operacao=${encodeURIComponent(operacaoSessao)}` : '';
            api.get(`/api/producao/${op.id}/sessao${query}`)
                .then(({ ordem: ordemEscopada }) => {
                    setOp((o) => (o ? { ...o, quantidadeAProduzir: Number(ordemEscopada.meta_por_operacao?.[operacaoSessao] ?? ordemEscopada.quantidade) } : o));
                    setProduzidoAcumulado(Number(ordemEscopada.produzido_total) || 0);
                    setRefugadoAcumulado(Number(ordemEscopada.refugado_total) || 0);
                    setProduzidoPorOperacao(ordemEscopada.produzido_por_operacao || {});
                    // Setup usa ZS1_HRIREA. A consulta de sessão abaixo é
                    // baseada na ZH6 e não pode apagar o horário já carregado.
                    if (!ehTipoSetup(tipoHoraAtiva)) {
                        setHoraInicio(ordemEscopada.hora_inicio_turno || '--:--');
                    }
                    setParadasCount(ordemEscopada.paradas_count || 0);
                    setImagemPeca(apiUrl(ordemEscopada.imagem_url));
                    setDesenhoPeca(apiUrl(ordemEscopada.desenho_url));
                    setPfiPeca(apiUrl(ordemEscopada.pfi_url));
                    setSetupFeito(!!ordemEscopada.setup_feito);
                })
                .catch(() => { }); // resiliente — só tenta de novo no próximo tick, sem incomodar o operador
        }, 15000);
        return () => clearInterval(intervalId);
    }, [op?.id, operacaoSessao, modal, tipoHoraAtiva]);

    // Serve pra começar do zero (status AGUARDANDO) e pra retomar depois de uma
    // pausa (status PARADO) — mesmo botão, mesma lógica (decisão do usuário
    // 05/08/2026: não existe mais "Continuar Produção" separado). Só zera
    // hora de início/tempo/paradas quando é de fato um início novo; na
    // retomada, esses valores já estão certos no estado local (preservados
    // desde a pausa) e o backend também preserva na ZH6.
    // Tipo de Hora (21/08/2026; revertido em 17/09/2026, pedido do usuário) —
    // clique em "Iniciar Produção" sem tipo escolhido abre o ModalTipoHora
    // (handleIniciarClick); confirmar lá (handleConfirmarTipoHora) já chama
    // executarInicio na hora, sem exigir um segundo clique em "Iniciar
    // Produção". tipoHoraSessao continua existindo só pro atalho de Projeto
    // Especial abaixo (especial && setupFeito), que não passa pelo modal.
    const handleIniciarClick = () => {
        if (iniciando) return;
        if (!tipoHoraSessao) {
            // Projeto Especial (29/08/2026, pedido do usuário) — depois do
            // setup feito, vai direto pro código 4 (Produção Especial), sem
            // abrir o ModalTipoHora. Cobre tanto a 1ª vez (setModal já não
            // abriu, ver handleSelecionarOP) quanto o instante em que o setup
            // acabou de ser concluído nesta mesma sessão (setSetupFeito(true)
            // local, ver handleConfirmarPausa/executarInicio).
            if (especial && setupFeito) {
                setTipoHoraSessao('4');
                return;
            }
            setModal('tipoHora');
            return;
        }
        executarInicio(tipoHoraSessao);
        setTipoHoraSessao('');
    };
    const carregarMateriaisOP = async (opId = op?.id, { abrirModal = true } = {}) => {
        setMateriaisOP([]);
        setErroMateriais('');
        setCarregandoMateriais(true);
        if (abrirModal) setModal('materiais');
        try {
            const resposta = await api.get(`/api/producao/${opId}/materiais`);
            setMateriaisOP(resposta.materiais || []);
            return {
                materiais: resposta.materiais || [],
                ja_requisitado: !!resposta.ja_requisitado,
            };
        } catch (err) {
            setErroMateriais(err.message || 'Não foi possível consultar os materiais da OP.');
            return { materiais: [], ja_requisitado: false };
        } finally {
            setCarregandoMateriais(false);
        }
    };
    const handleAbrirNovaRequisicao = () => {
        setRequisicaoDuranteApontamento(true);
        carregarMateriaisOP(op?.id);
    };
    const handleConfirmarTipoHora = (tipoHora) => {
        setModal(null);
        executarInicio(tipoHora);
    };
    const handleConfirmarMateriais = async ({ modo, itens }) => {
        setErroMateriais('');
        setIniciando(true);
        try {
            await api.post(`/api/producao/${op.id}/requisitar-materiais`, {
                operacao: operacaoSessao,
                modo,
                itens,
            });
            const atualizados = await api.get(`/api/producao/${op.id}/materiais`);
            setMateriaisOP(atualizados.materiais || []);
            setModal(requisicaoDuranteApontamento ? null : 'tipoHora');
            setRequisicaoDuranteApontamento(false);
        } catch (err) {
            setErroMateriais(mensagemRequisicaoMateriais(err, materiaisOP, itens));
        } finally {
            setIniciando(false);
        }
    };
    // Volta pra tela de escolha de OP (mesmo reset do botão "Trocar OP" no
    // cabeçalho, ver abaixo) — usado pelo ModalTipoHora quando o setup é
    // obrigatório e o operador cancela/fecha o aviso: não faz sentido deixar
    // ele preso na tela de apontamentos de uma OP que ele não pode operar
    // ainda (25/08/2026, pedido do usuário).
    const handleVoltarSelecaoOP = () => {
        setOpPreview(null);
        setSequenciaEscolhida(null);
        setOperacoesRoteiro([]);
        setHistorico([]);
        setProduzidoPorOperacaoPreview({});
        setMetaPorOperacaoPreview({});
        setReatribuicoesPorOperacaoPreview({});
        setModal('selecao');
    };
    const tratarOpEncerrada = (err) => {
        // Nem todo 409 significa encerramento (também há conflitos de teto,
        // sessão etc.); o código do backend evita mostrar a mensagem errada.
        if (err?.code !== 'OP_ENCERRADA') return false;
        setErroInicioModal(err.message || 'Esta OP já está encerrada e não aceita novos apontamentos.');
        setModal('erroInicio');
        return true;
    };
    const executarInicio = async (tipoHora) => {
        // REABERTA (14/08/2026) entra como início novo também — o ciclo
        // anterior já foi fechado (marcado Concluída antes de reabrir por
        // refugo posterior), não tem nada de "pausa" pra retomar.
        const fresh = status === 'AGUARDANDO' || status === 'REABERTA';
        const horaParaEnviar = fresh ? getNow() : horaInicio;
        setErroApi('');
        setIniciando(true);
        try {
            const respostaInicio = await api.post(`/api/producao/${op.id}/iniciar`, { hora_inicio_turno: horaParaEnviar, operador: operadorLogado?.nome || '—', recurso: recursoSessao, operacao: operacaoSessao, tipo_hora: tipoHora });
            setTipoHoraAtiva(tipoHora);
            if (fresh) {
                setHoraInicio(respostaInicio?.hora_inicio_turno || horaParaEnviar);
                resetElapsed(0);
                setParadasCount(0);
            }
            setRunning(true);
            setStatus('EM PRODUCAO');
            return true;
        } catch (err) {
            if (tratarOpEncerrada(err)) return false;
            // Requisição de materiais (01/09/2026) — a chamada acima já falha
            // ANTES de abrir a sessão (backend requisita o material antes de
            // gravar na ZH6, ver comentário em producao.js), então o operador
            // nunca vê "Em produção" com material não reservado — só precisa
            // de uma mensagem clara do que aconteceu, distinta do erro
            // genérico de "iniciar produção".
            if (err.code === 'OP_SEM_EMPENHOS') {
                setErroInicioModal(err.message || 'Esta OP ainda não possui materiais empenhados. Solicite ao PCP a geração dos empenhos antes de iniciar a produção.');
            } else if (err.code === 'PROTHEUS_REQUISICAO_MATERIAIS') {
                const mensagemEstoque = mensagemRequisicaoMateriais(err, materiaisOP);
                const mensagemComunicacao = err.estadoIndeterminado
                    ? 'O sistema não conseguiu se comunicar com o ERP para requisitar os materiais. A produção não foi iniciada. Avise o PCP ou o suporte antes de tentar novamente.'
                    : 'O sistema não conseguiu requisitar os materiais desta OP. Avise o PCP para verificar os empenhos e o estoque.';
                setErroInicioModal(mensagemEstoque || mensagemComunicacao);
            } else {
                setErroInicioModal(err.message || 'Erro ao iniciar produção.');
            }
            setModal('erroInicio');
            return false;
        } finally {
            setIniciando(false);
        }
    };
    // Setup (21/08/2026, pedido do usuário): Pausar Setup não pede motivo nem
    // quantidade — fecha o ciclo direto com os valores zerados, já que
    // Preparação não produz peça nenhuma (reaproveita handleConfirmarPausa,
    // mesma função de sempre, só sem passar pelo ModalPausa).
    const handlePausarClick = () => {
        if (status !== 'EM PRODUCAO') return;
        // motivo por tipo (27/08/2026) — antes gravava "Setup" fixo mesmo
        // durante uma Repreparação; agora reflete qual dos dois é de verdade.
        if (emSetup) { handleConfirmarPausa({ motivo: tipoHoraRelevante === '6' ? 'Repreparação' : 'Setup', obs: '', produzido: 0, refugado: 0, justificativa: undefined }); return; }
        setModal('pausa');
    };
    const handleConfirmarPausa = async ({ motivo, obs, produzido, refugado, justificativa }) => {
        const novoProduzido = produzidoAcumulado + produzido;
        const novoStatus = novoProduzido >= op.quantidadeAProduzir ? 'CONCLUIDA' : 'PARADO';
        const novasParadas = paradasCount + 1;
        try {
            // Estado de sessão resultante (status/tempo/paradas) vai junto nesse
            // mesmo POST — registrarApontamentoZH6 grava por cima da linha aberta
            // do ciclo atual (ver zh6Writer.js), não cria uma linha nova.
            const { apontamento } = await api.post(`/api/producao/${op.id}/apontar`, {
                produzido, refugado, motivo, observacao: obs, operador: operadorLogado?.nome || '—', inicio: horaInicio, tempo_segundos: elapsedRef.current, operacao: operacaoSessao, recurso: recursoSessao,
                tempo_acumulado_segundos: elapsedRef.current, paradas_count: novasParadas, status: novoStatus, justificativa,
                // Setup (21/08/2026) — avisa o backend que este ciclo é de
                // Preparação (tipoHoraAtiva, ver executarInicio), pra ele
                // gravar na ZS1 em vez da ZH6 (ver criarApontamento).
                tipo_hora: tipoHoraAtiva,
            });
            // Setup (21/08/2026) NÃO entra no histórico de apontamentos —
            // esse histórico é só o que veio da ZH6 (apontamentos de
            // verdade); o fechamento de um ciclo de setup mora na ZS1 (ver
            // setup.js), fora daqui, então não simula uma entrada aqui.
            if (tipoHoraAtiva !== '3') setHistorico(h => [mapApontamento(apontamento), ...h]);
            setProduzidoAcumulado(novoProduzido);
            setRefugadoAcumulado(r => r + refugado);
            // Zera na tela assim que pausa (pedido do usuário 06/08/2026) — o
            // valor real desse ciclo (elapsed/novasParadas) já foi enviado e
            // gravado acima, só o mostrado na tela reseta; se retomar depois,
            // o próximo ciclo começa do zero, não carrega esse tempo/paradas.
            resetElapsed(0);
            setParadasCount(0);
            setHoraInicio('--:--');
            setRunning(false);
            setStatus(novoStatus);
            setModal(null);
            // Setup obrigatório (21/08/2026): se o ciclo que acabou de fechar
            // era de Preparação, libera os outros tipos de hora já aqui, sem
            // esperar o próximo refresh da sessão (ver comentário completo em
            // executarInicio/obterSessao).
            if (tipoHoraAtiva === '3') setSetupFeito(true);
        } catch (err) {
            if (tratarOpEncerrada(err)) return;
            setErroApi(err.message || 'Erro ao registrar a pausa.');
        }
    };
    // Só apontar de fato "Em Produção" — pausada precisa retomar (Iniciar
    // Produção) antes de poder apontar de novo, senão não tem um "desde
    // quando" claro pro tempo do apontamento.
    // Setup (21/08/2026, pedido do usuário): "Liberar Máquina" não pede
    // quantidade produzida/refugada nem passa por ModalQuantidades/ModalResumo
    // — fecha o ciclo direto com 0/0 (handleLiberarMaquina, abaixo).
    const handleApontarClick = () => {
        if (status !== 'EM PRODUCAO') return;
        // Liberar Máquina (25/08/2026): não fecha o setup direto mais, abre a
        // confirmação primeiro (ver ModalConfirmarLiberarMaquina/
        // handleConfirmarLiberarMaquina).
        if (emSetup) { setModal('confirmarLiberar'); return; }
        setModal('apontamento');
    };
    const handleConfirmarApontamento = ({ produzido, refugado, justificativa }) => {
        const operacaoLabel = operacaoSessao ? `${operacaoSessao} · ${recursoCodigoNomeSessao}` : '—';
        setDadosResumo({ operador: operadorLogado?.nome || '—', maquina: recursoCodigoNomeSessao || op.maquina, produto: op.produtoNome, op: op.codigo, produzido, refugado, justificativa, operacaoLabel, tempo: formatTime(elapsedRef.current), inicio: horaInicio, paradas: paradasCount });
        setModal('resumo');
    };
    const handleConcluirApontamento = async () => {
        const novoProduzido = produzidoAcumulado + dadosResumo.produzido;
        const concluida = novoProduzido >= op.quantidadeAProduzir;
        // "Apontar Produção" fecha o ciclo atual, igual uma pausa — volta pra
        // "Aguardando" e exige clicar em "Iniciar Produção" de novo, em vez de
        // continuar rodando com o botão de "Pausar" (cada apontamento é um
        // registro fechado, não queremos o cronômetro seguindo escondido).
        const statusResultante = concluida ? 'CONCLUIDA' : 'AGUARDANDO';
        const tempoResultante = concluida ? elapsedRef.current : 0;
        const paradasResultante = concluida ? paradasCount : 0;
        try {
            const { apontamento } = await api.post(`/api/producao/${op.id}/apontar`, {
                produzido: dadosResumo.produzido, refugado: dadosResumo.refugado, operador: dadosResumo.operador, inicio: horaInicio, tempo_segundos: elapsedRef.current, operacao: operacaoSessao, recurso: recursoSessao,
                tempo_acumulado_segundos: tempoResultante, paradas_count: paradasResultante, status: statusResultante, justificativa: dadosResumo.justificativa,
                tipo_hora: tipoHoraAtiva,
            });
            setHistorico(h => [mapApontamento(apontamento), ...h]);
            setProduzidoAcumulado(novoProduzido);
            setRefugadoAcumulado(r => r + dadosResumo.refugado);
            setStatus(statusResultante);
            setRunning(false);
            if (!concluida) {
                resetElapsed(0); setParadasCount(0); setHoraInicio('--:--');
            }
            setModal(null);
            setDadosResumo(null);
            // Setup obrigatório (21/08/2026) — mesmo tratamento de handleConfirmarPausa.
            if (tipoHoraAtiva === '3') setSetupFeito(true);
        } catch (err) {
            if (tratarOpEncerrada(err)) return;
            setErroApi(err.message || 'Erro ao concluir o apontamento.');
        }
    };
    // "Liberar Máquina" (21/08/2026) — mesma ideia de handleConcluirApontamento,
    // só que direto (sem ModalQuantidades/ModalResumo): Preparação não produz
    // peça, então produzido/refugado sempre 0, sem pedir nada ao operador.
    // Devolve true/false para que o chamador consiga distinguir a liberação
    // concluída de uma falha da API.
    const handleLiberarMaquina = async () => {
        const novoProduzido = produzidoAcumulado; // +0 — setup não produz peça.
        const concluida = novoProduzido >= op.quantidadeAProduzir;
        const statusResultante = concluida ? 'CONCLUIDA' : 'AGUARDANDO';
        const tempoResultante = concluida ? elapsedRef.current : 0;
        const paradasResultante = concluida ? paradasCount : 0;
        try {
            const { apontamento } = await api.post(`/api/producao/${op.id}/apontar`, {
                produzido: 0, refugado: 0, operador: operadorLogado?.nome || '—', inicio: horaInicio, tempo_segundos: elapsedRef.current, operacao: operacaoSessao, recurso: recursoSessao,
                tempo_acumulado_segundos: tempoResultante, paradas_count: paradasResultante, status: statusResultante, justificativa: undefined,
                tipo_hora: tipoHoraAtiva,
            });
            // Setup não entra no histórico de apontamentos (ver mesma nota em
            // handleConfirmarPausa) — "Liberar Máquina" é sempre um
            // fechamento de setup, então nunca chama setHistorico aqui.
            setStatus(statusResultante);
            setRunning(false);
            if (!concluida) {
                resetElapsed(0); setParadasCount(0); setHoraInicio('--:--');
            }
            setSetupFeito(true);
            // O setup terminou, mas a OP continua selecionada. Limpar o tipo
            // ativo faz a própria tela trocar imediatamente para os controles
            // de produção: Iniciar habilitado e Apontar ainda bloqueado.
            setTipoHoraAtiva('');
            setTipoHoraSessao('');
            return true;
        } catch (err) {
            if (tratarOpEncerrada(err)) return false;
            setErroApi(err.message || 'Erro ao liberar a máquina.');
            return false;
        }
    };

    // Confirmação antes de liberar (25/08/2026, pedido do usuário) — clicar em
    // "Liberar Máquina" não fecha o setup direto mais, só abre um modal
    // perguntando se o setup foi feito de verdade e a máquina está pronta.
    // Cancelar: fecha o modal, nada mais muda — o cronômetro do setup continua
    // rodando normalmente (nenhum estado foi tocado). Confirmar: encerra o
    // setup e mantém a OP na tela, agora pronta para iniciar a produção.
    const handleConfirmarLiberarMaquina = async () => {
        setModal(null);
        await handleLiberarMaquina();
    };

    const handleEncerrarOP = async () => {
        setErroEncerramento('');
        setEncerrandoOP(true);
        try {
            await api.post(`/api/producao/${op.id}/encerrar`, { operacao: operacaoSessao });
            setModal(null);
            handleVoltarSelecaoOP();
        } catch (err) {
            const detalhes = Array.isArray(err.detalhes) ? `\n${err.detalhes.join('\n')}` : '';
            const aviso = err.estadoIndeterminado
                ? '\nNão foi possível confirmar o estado final. Consulte a OP no ERP antes de tentar novamente.'
                : '';
            setErroEncerramento(`${err.message || 'Não foi possível encerrar a OP.'}${detalhes}${aviso}`);
        } finally {
            setEncerrandoOP(false);
        }
    };


// Badge por tipo de hora (27/08/2026, pedido do usuário) — antes só
// distinguia Produção (verde) de Setup (roxo, cobrindo Preparação+Repreparação
// juntas, ver `emSetup`); agora existem 4 rótulos, um por tipo de hora
// selecionável (TIPOS_HORA/ModalTipoHora). `status` internamente continua só
// 'EM PRODUCAO' pra todo mundo (não dá pra trocar esse valor sem reescrever
// todo gate que compara `status === 'EM PRODUCAO'' nesta tela) — quem decide
// o RÓTULO/cor exibido aqui é tipoHoraRelevante, não o status em si.
const BADGE_POR_TIPO_HORA = {
    '1': { cls: 'bg-green-500', label: 'Em Produção', icon: <Play size={14} fill="white" /> },
    '2': { cls: 'bg-blue-500', label: 'Em Retrabalho', icon: <AlertTriangle size={14} /> },
    '3': { cls: 'bg-roxo-500', label: 'Em Setup', icon: <Settings size={14} /> },
    '6': { cls: 'bg-indigo-500', label: 'Em Repreparação', icon: <RefreshCw size={14} /> },
    // Produção Especial (29/08/2026) — mesmo amarelo do destaque de Projeto
    // Especial no cabeçalho, pra reforçar visualmente que é a mesma coisa.
    '4': { cls: 'bg-amber-500', label: 'Em Produção Especial', icon: <Star size={14} /> },
};
const statusBadge = {
    'AGUARDANDO': { cls: 'bg-gray-400', label: 'Aguardando', icon: <Clock size={14} /> },
    'EM PRODUCAO': BADGE_POR_TIPO_HORA[tipoHoraRelevante] || BADGE_POR_TIPO_HORA['1'],
    'PARADO': { cls: 'bg-orange-500', label: 'Pausada', icon: <Pause size={14} fill="white" /> },
    'CONCLUIDA': { cls: 'bg-petrol-500', label: 'Concluída', icon: <CheckCircle size={14} /> },
    // 14/08/2026 — só a 1ª sequência do roteiro pode voltar pra esse status:
    // já foi "Concluída", mas refugo registrado numa sequência posterior
    // aumentou a meta dela (ver meta_por_operacao/obterSessao no backend).
    'REABERTA': { cls: 'bg-roxo-500', label: 'Reaberta', icon: <RefreshCw size={14} /> },
}[status] || { cls: 'bg-gray-400', label: status || 'Aguardando', icon: <Clock size={14} /> };

const AVISO_COR = { red: 'text-red-400', violet: 'text-roxo-400', petrol: 'text-petrol-400' };

// Máquina precisa estar resolvida antes de mais nada — vem do login (ver
// useAuthStore.login). Se não vier resolvida (mais de uma candidata, ou
// nenhuma com OP em aberto agora, ou persistência antiga sem a lista), a
// telinha de escolha aparece SEMPRE — o operador escolhe a máquina, entra no
// ModalSelecionarOP (OP → sequência → Confirmar Seleção) e segue pro
// apontamento, tudo dentro da própria aba Produção. Nunca mais trava com o
// aviso "Seu usuário não tem nenhuma máquina liberada." (17/09/2026, pedido
// do usuário).
if (!operadorLogado?.maquina) {
    const candidatas = operadorLogado?.maquinasDisponiveis || [];
    return (
        <ModalSelecionarMaquinaLogin
            maquinas={candidatas}
            carregando={carregandoMaquinas}
            onConfirmar={(m) => definirMaquina(m.codigo, m.nome)}
            onVerificar={buscarMaquinasUsuario}
            nomeUsuario={operadorLogado?.nome}
            onLogout={handleLogout}
        />
    );
}

if (!op) {
    if (carregandoOrdens || verificandoEmAndamento) {
        return (
            <div className="h-[calc(100vh-6rem)] flex items-center justify-center text-anthracite-500 text-sm">
                Carregando ordens de produção...
            </div>
        );
    }
    if (opEmAndamento) {
        return (
            <ModalOpEmAndamento
                codigo={opEmAndamento.codigo}
                produtoNome={opEmAndamento.ordem?.produto_nome || opEmAndamento.ordem?.produto}
                isSetup={!!opEmAndamento._setup}
                // Só passa quando é uma máquina DIFERENTE da atual (25/08/2026)
                // — mensagem genérica "nesta máquina" já cobre o caso comum
                // (mesma máquina), só precisa citar o nome quando for outra.
                maquinaDiferente={opEmAndamento._recurso && opEmAndamento._recurso !== recursoSessao ? opEmAndamento._recursoNome : null}
                onConfirmar={handleConfirmarOpEmAndamento}
                nomeUsuario={operadorLogado?.nome}
                onLogout={handleLogout}
            />
        );
    }
    // Painel de sequência (direita) já entra junto com o de OP (esquerda) —
    // aparece assim que clica numa OP (handlePreVisualizarOP), sem precisar
    // confirmar antes. Só libera "Confirmar Seleção" quando a OP não tem
    // sequência pra escolher (resiliência de sempre) ou quando já escolheu uma.
    const podeConfirmarOP = !!opPreview && !carregandoOperacoes && (candidatosSequencia.length === 0 || !!sequenciaEscolhida);
    return (
        <ModalSelecionarOP
            ordens={ordensDisponiveis}
            selecionada={opPreview}
            onSelecionarOP={handlePreVisualizarOP}
            onConfirmar={() => handleSelecionarOP(opPreview)}
            podeConfirmar={podeConfirmarOP}
            maquinaNome={recursoCodigoNomeSessao}
            // Sempre disponível (13/08/2026, não mais só quando maquinasDisponiveis.length > 1) —
            // agora busca ao vivo (handleTrocarMaquina), então vale a pena mesmo quando só
            // havia 1 candidata no login: pode ter surgido outra com OP aberta desde então.
            onTrocarMaquina={handleTrocarMaquina}
            carregandoSequencias={carregandoOperacoes}
            sequencias={candidatosSequencia}
            sequenciaEscolhida={sequenciaEscolhida}
            onEscolherSequencia={setSequenciaEscolhida}
            semOperacaoNoRoteiro={semOperacaoNoRoteiro}
            aguardandoEtapaAnterior={aguardandoEtapaAnterior}
            todasOperacoesJaConcluidas={todasOperacoesJaConcluidas}
            produzidoPorOperacaoPreview={produzidoPorOperacaoPreview}
            metaPorOperacaoPreview={metaPorOperacaoPreview}
            nomeUsuario={operadorLogado?.nome}
            onLogout={handleLogout}
        />
    );
}

return (
    // Padding superior reduzido (18/08/2026, pedido do usuário: "subir esses
    // cards pra mais perto da topbar") — pt menor que px/pb, só nesta tela
    // (Modo Operador), aproximando o cabeçalho de status do topo sem grudar
    // nele. gap-3→gap-2 e overflow mantido só como rede de segurança (nunca
    // reduzir a ponto de esconder "Iniciar Produção"/"Apontar Produção" numa
    // janela pequena ou com zoom do navegador) — testado sem necessidade de
    // rolagem em várias alturas de janela reais (1600x650 até 1920x1040).
    <div className="h-[calc(100vh-6rem)] lg:h-[calc(100vh-7rem)] bg-surface-0 pt-1.5 px-3 pb-3 lg:pt-2 lg:px-5 lg:pb-5 flex flex-col gap-2 overflow-y-auto">
        {/* Planejamento diario desativado temporariamente para a implantacao em producao. */}
        {/* <MeuPlanejamento /> */}
        {/* Avisos (18/08/2026, pedido do usuário: os blocos coloridos de largura
            total empilhados no topo competiam demais por atenção, e um deles
            ainda piscava por engano, ver remoção mais abaixo). Agora só 2 lugares:
            (1) erro de API vira um toast que some sozinho, canto inferior direito,
            não empurra mais o resto do conteúdo; (2) o aviso contextual da
            operação atual (`avisoAtivo`, definido acima) vira uma linha só,
            anexada ao próprio cabeçalho de status logo abaixo, não mais um bloco
            à parte competindo com ele. semOperacaoNoRoteiro/aguardandoEtapaAnterior/
            todasOperacoesJaConcluidas não aparecem mais aqui (achado real do
            usuário: o de "aguardando liberação" piscava, contradizendo o aviso de
            baixo que já mostrava o teto certo) — os 3 são sobre QUAL sequência
            oferecer pra escolher, só fazem sentido dentro do ModalSelecionarOP
            (onde já existem, ver linhas ~322-330), nunca com uma sequência já
            ativa e rodando. */}
        {erroApi && (
            <div className="fixed bottom-5 right-5 z-50 flex items-center gap-3 rounded-xl px-4 py-3 text-[12px] font-medium bg-red-500/10 border border-red-500/15 text-red-400 shadow-lg max-w-sm animate-slide-up">
                <AlertTriangle size={16} className="shrink-0" />
                <span className="flex-1">{erroApi}</span>
                <button onClick={() => setErroApi('')} className="hover:text-red-200 shrink-0"><X size={14} /></button>
            </div>
        )}
        {/* Header */}
        <div className="shrink-0 mes-card px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                    <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-white font-semibold text-xs shrink-0 ${statusBadge.cls}`}>
                        {statusBadge.icon} {statusBadge.label}
                    </div>
                    {/* Projeto Especial (29/08/2026, pedido do usuário: "a OP deve vir
                        na tela de apontamentos em amarelo para que o operador veja
                        que é uma produção especial") — SC2.C2_ZESPECI='2'. */}
                    {especial && (
                        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-amber-500 text-white font-semibold text-xs shrink-0">
                            <Star size={13} /> Projeto Especial
                        </div>
                    )}
                    {/* Operação (27/08/2026, pedido do usuário: "preciso de um lugar
                        pra colocar a informação de qual é aquela operação") — antes
                        não aparecia em nenhum lugar fixo da tela, só implícita na
                        escolha de sequência lá no início. */}
                    {operacaoSessao && (
                        <div className="flex items-center px-2.5 py-1.5 rounded bg-surface-3/60 text-anthracite-300 font-semibold text-xs shrink-0 tabular-nums">
                            Operação {operacaoSessao}
                        </div>
                    )}
                    <div className="min-w-0">
                        <h1 className="text-base font-bold text-gray-800 dark:text-white truncate">
                            {op.codigo} <span className="text-gray-300 dark:text-gray-600 mx-1">·</span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">{op.produtoNome}</span>
                        </h1>
                        <p className="text-xs text-gray-400 dark:text-gray-500 truncate flex items-center gap-1 mt-0.5">
                            <User size={11} /> {operadorLogado?.nome || '—'} · {recursoCodigoNomeSessao || op.maquina}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => setModal('historico')} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/[0.06] text-anthracite-400 text-[12px] font-medium hover:bg-white/[0.04] transition-colors">
                        <History size={14} /> <span className="hidden sm:inline">Histórico</span>
                    </button>
                    <button onClick={() => {
                        setModal('documentacao');
                        setCarregandoDocumentos(true);
                        api.get(`/api/producao/documentos/${encodeURIComponent(op.produto)}`)
                            .then((r) => setDocumentosOP(r.documentos || []))
                            .catch(() => setDocumentosOP([]))
                            .finally(() => setCarregandoDocumentos(false));
                    }} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/[0.06] text-anthracite-400 text-[12px] font-medium hover:bg-white/[0.04] transition-colors">
                        <FolderOpen size={14} /> <span className="hidden sm:inline">Documentação</span>
                    </button>
                    {podeFazerNovaRequisicao && (
                        <button onClick={handleAbrirNovaRequisicao} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/[0.06] text-anthracite-400 text-[12px] font-medium hover:bg-white/[0.04] transition-colors">
                            <Layers size={14} /> <span className="hidden sm:inline">Requisitar material</span>
                        </button>
                    )}
                    <button onClick={handleVoltarSelecaoOP} className="flex items-center gap-1.5 px-3 py-2 rounded-xl border border-white/[0.06] text-anthracite-400 text-[12px] font-medium hover:bg-white/[0.04] transition-colors">
                        <RefreshCw size={14} /> <span className="hidden sm:inline">Trocar OP</span>
                    </button>
                </div>
            </div>
            {avisoAtivo && (
                <div className={`mt-3 pt-3 border-t border-white/[0.04] flex items-start gap-2 text-[12px] font-medium ${AVISO_COR[avisoAtivo.tipo]}`}>
                    <avisoAtivo.Icone size={14} className="shrink-0 mt-0.5" />
                    <span>{avisoAtivo.texto}</span>
                </div>
            )}
        </div>

        {/* Main */}
        {/* Proporção 3:2 -> 2:1 (27/08/2026, pedido do usuário: "deixar a parte
            do PDF das imagens maior") — o painel de PDF/imagens ganhou mais
            largura; o painel de informações ficou mais estreito e com menos
            espaçamento entre os blocos (padding/gap menores), mas os textos e
            números dos cards continuam do MESMO tamanho de antes — só a
            folga ao redor deles diminuiu, não o conteúdo em si. */}
        <div className="flex-1 min-h-0 flex flex-col lg:flex-row gap-3">
            <div className="mes-card p-4 flex flex-col flex-1 lg:flex-[2] min-h-0">
                <MediaPanel pfiPeca={pfiPeca} imagemPeca={imagemPeca} desenhoPeca={desenhoPeca} produtoCodigo={op.produto} produtoDescricao={op.produtoNome} />
            </div>
            <div className="mes-card p-2.5 flex flex-col gap-1.5 flex-1 lg:flex-1 min-h-0">
                <div className="flex-[3] grid grid-cols-2 grid-rows-2 gap-1.5 min-h-0">
                    <BigStat label="Quantidade a produzir" value={op.quantidadeAProduzir} icon={Target} accent="gray" />
                    <BigStat label="Produzido" value={produzidoAcumulado} icon={CheckCircle} accent="green" />
                    <BigStat label="Saldo" value={saldo} icon={Layers} accent="blue" />
                    <BigStat label="Refugos" value={refugadoAcumulado} icon={AlertTriangle} accent="red" />
                </div>
                <div className="shrink-0 bg-surface-3/30 rounded-xl border border-white/[0.03] p-2">
                    <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] text-anthracite-500">Progresso da Ordem</span>
                        <span className="text-[12px] font-bold text-brand-400 tabular-nums">{progresso.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-surface-3 rounded-full h-2 overflow-hidden">
                        <div className="bg-brand-500 h-full rounded-full transition-all duration-500" style={{ width: `${progresso}%` }} />
                    </div>
                </div>
                <div className="flex-[2] grid grid-cols-3 gap-1.5 min-h-0">
                    <BigStat label="Início" value={horaInicio} icon={Calendar} compact />
                    <Timer key={timerResetKey} running={running} initialValue={elapsedRef.current} onTick={(v) => { elapsedRef.current = v; }} />
                    <BigStat label="Paradas" value={paradasCount} icon={Pause} compact />
                </div>
            </div>
        </div>

        {/* Actions */}
        {carregandoSessao ? (
            // Enquanto a sessão real ainda não chegou, nem mostra os botões —
            // os rótulos (Iniciar Setup/Iniciar Produção) dependem de
            // setupFeito, que só fica certo depois da resposta da API (ver
            // comentário na declaração de carregandoSessao). Melhor um
            // placeholder neutro do que um botão com o texto errado.
            <div className="flex items-center justify-center gap-2 py-5 rounded-xl text-[13px] font-medium text-anthracite-500 bg-surface-3/40">
                Carregando sessão desta OP...
            </div>
        ) : (
        <div className="flex gap-3">
            {/* Sem saldo (14/08/2026, pedido do usuário): se essa operação já
                apontou tudo que dava (seja o que a anterior entregou, seja a
                própria meta dela), não faz sentido deixar "Iniciar Produção"
                habilitado — o operador começa uma sessão que nunca vai
                conseguir apontar nada (bloqueado no servidor de qualquer
                forma, ver criarApontamento), só confunde. */}
            {/* Um único botão/id pras 3 situações (AGUARDANDO/REABERTA/PARADO) —
                não existe "Continuar Produção" separado (decisão do usuário,
                ver nota no topo de zh6Writer.js): mesmo botão, mesmo rótulo,
                mesma função (handleIniciarClick), esteja a OP começando do
                zero ou retomando depois de uma pausa. */}
            {(status === 'AGUARDANDO' || status === 'REABERTA' || status === 'PARADO') && (
                <button id="btn-iniciar-producao" onClick={handleIniciarClick}
                    disabled={iniciarDesabilitado || iniciando}
                    title={iniciarTooltip}
                    className="flex-1 flex items-center justify-center gap-2 py-5 rounded-xl font-semibold text-[15px] text-white bg-petrol-600 hover:bg-petrol-500 transition-colors shadow-[0_1px_3px_rgba(24,179,122,0.3)] disabled:bg-surface-3 disabled:text-anthracite-500 disabled:shadow-none disabled:cursor-not-allowed">
                    <Play size={19} fill="white" /> {iniciando ? 'Iniciando...' : rotuloIniciar}
                </button>
            )}
            {status === 'EM PRODUCAO' && (
                <button id="btn-pausar-producao" onClick={handlePausarClick}
                    className="flex-1 flex items-center justify-center gap-2 py-5 rounded-xl font-semibold text-[15px] text-white bg-amber-600 hover:bg-amber-500 transition-colors">
                    <Pause size={19} fill="white" /> {rotuloPausar}
                </button>
            )}
            {status === 'CONCLUIDA' && (
                <button onClick={handleVoltarSelecaoOP}
                    className="flex-1 flex items-center justify-center gap-2 py-5 rounded-xl font-semibold text-[15px] text-white bg-surface-3 hover:bg-surface-4 transition-colors">
                    <RefreshCw size={19} /> Selecionar Nova OP
                </button>
            )}
            <button id="btn-apontar-producao" onClick={handleApontarClick}
                disabled={status !== 'EM PRODUCAO'}
                className={`flex-[1.3] flex items-center justify-center gap-2 py-5 rounded-xl font-semibold text-[15px] text-white transition-colors
                        ${status === 'EM PRODUCAO' ? 'bg-petrol-600 hover:bg-petrol-500' : 'bg-surface-3 text-anthracite-500 cursor-not-allowed'}`}>
                <CheckCircle size={19} /> {rotuloApontar}
            </button>
            {naUltimaOperacao && !running && produzidoAcumulado > 0 && (
                <button onClick={() => { setErroEncerramento(''); setModal('encerrarOP'); }}
                    className="flex-1 flex items-center justify-center gap-2 py-5 rounded-xl font-semibold text-[15px] text-white bg-red-600 hover:bg-red-500 transition-colors">
                    <Lock size={18} /> Encerrar OP
                </button>
            )}
        </div>
        )}

        {modal === 'selecao' && (
            <ModalSelecionarOP
                ordens={ordensDisponiveis}
                selecionada={opPreview}
                onSelecionarOP={handlePreVisualizarOP}
                onConfirmar={() => handleSelecionarOP(opPreview)}
                podeConfirmar={!!opPreview && !carregandoOperacoes && (candidatosSequencia.length === 0 || !!sequenciaEscolhida)}
                onFechar={() => { setModal(null); setOpPreview(null); }}
                maquinaNome={recursoCodigoNomeSessao}
                // Sempre disponível (13/08/2026, não mais só quando maquinasDisponiveis.length > 1) —
            // agora busca ao vivo (handleTrocarMaquina), então vale a pena mesmo quando só
            // havia 1 candidata no login: pode ter surgido outra com OP aberta desde então.
            onTrocarMaquina={handleTrocarMaquina}
                carregandoSequencias={carregandoOperacoes}
                sequencias={candidatosSequencia}
                sequenciaEscolhida={sequenciaEscolhida}
                onEscolherSequencia={setSequenciaEscolhida}
                semOperacaoNoRoteiro={semOperacaoNoRoteiro}
                aguardandoEtapaAnterior={aguardandoEtapaAnterior}
                todasOperacoesJaConcluidas={todasOperacoesJaConcluidas}
                produzidoPorOperacaoPreview={produzidoPorOperacaoPreview}
                metaPorOperacaoPreview={metaPorOperacaoPreview}
                nomeUsuario={operadorLogado?.nome}
                onLogout={handleLogout}
            />
        )}
        {modal === 'tipoHora' && <ModalTipoHora onConfirmar={handleConfirmarTipoHora} onFechar={() => setModal(null)} onVoltarSelecao={handleVoltarSelecaoOP} tipoHoraAtual={tipoHoraSessao} exigeSetup={!setupFeito} podePreparar={podePreparar} />}
        {EXIBIR_MATERIAIS_OP && modal === 'materiais' && <ModalMateriaisOP materiais={materiaisOP} carregando={carregandoMateriais} erro={erroMateriais} onTentarNovamente={() => carregarMateriaisOP()} onConfirmar={handleConfirmarMateriais} onFechar={() => { if (requisicaoDuranteApontamento) { setRequisicaoDuranteApontamento(false); setModal(null); } else { setTipoHoraSessao(''); handleVoltarSelecaoOP(); } }} iniciando={iniciando} projetoEspecial={tipoHoraSessao === '4'} />}
        {modal === 'erroInicio' && <ModalErroInicio mensagem={erroInicioModal} onConfirmar={() => { setErroInicioModal(''); setErroApi(''); handleVoltarSelecaoOP(); }} />}
        {modal === 'encerrarOP' && <ModalEncerrarOP op={op} produzido={produzidoAcumulado} parcial={produzidoAcumulado < op.quantidadeAProduzir} processando={encerrandoOP} erro={erroEncerramento} onConfirmar={handleEncerrarOP} onFechar={() => { setErroEncerramento(''); setModal(null); }} />}
        {modal === 'pausa' && <ModalPausa onConfirmar={handleConfirmarPausa} onFechar={() => setModal(null)} limiteFisico={saldoOperacaoAnterior} limiteMeta={saldo} descontaRefugadoDoTeto={!!operacaoAnterior} />}
        {modal === 'apontamento' && <ModalQuantidades onConfirmar={handleConfirmarApontamento} onFechar={() => setModal(null)} limiteFisico={saldoOperacaoAnterior} limiteMeta={saldo} descontaRefugadoDoTeto={!!operacaoAnterior} />}
        {modal === 'resumo' && dadosResumo && <ModalResumo dados={dadosResumo} onConfirmar={handleConcluirApontamento} onFechar={() => setModal('apontamento')} />}
        {modal === 'historico' && <ModalHistorico op={op} historico={historico} operacaoAtual={operacaoSessao} totalProduzido={produzidoAcumulado} totalRefugado={refugadoAcumulado} onFechar={() => setModal(null)} />}
        {modal === 'documentacao' && <ModalDocumentacao op={op} documentos={documentosOP} carregando={carregandoDocumentos} onFechar={() => setModal(null)} />}
        {modal === 'confirmarLiberar' && <ModalConfirmarLiberarMaquina onConfirmar={handleConfirmarLiberarMaquina} onFechar={() => setModal(null)} />}
    </div>
);
};

export default Producao;
